import React from 'react';
import ReactDOM from 'react-dom';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { strTrim, hasDefeitoAlert, normalizeHexColor, defeitoAlertTooltipText } from '../utils/defeitoAlertBadge';
import { getBoardClienteExibicao } from '../utils/clienteBoardDisplay';
import { createTechnicianRoadmapShare } from '../../technician-panel/services/technicianPanelApi';
import {
  openRoadmapSharePanel,
  routeShareFingerprint,
} from '../../technician-panel/utils/roadmapSharePanel';
import {
  buildRouteDefeitoPrintRows,
  enrichRouteOrdersDefeitoObs,
  escapeRoutePrintHtml,
  ROUTE_PRINT_DEFEITO_CSS,
} from '../utils/routePrintReportUtils';

const API_BASE_URL = getApiBaseUrl();

async function geocodeAddress(address) {
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/geocode/batch`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ addresses: [{ orderId: 'temp', address }] }),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (data.success && data.data && data.data.length > 0) {
      const result = data.data[0];
      if (result.lat && result.lng) {
        return { lat: result.lat, lng: result.lng, display_name: result.display_name || address };
      }
    }
    return null;
  } catch (error) {
    console.error('Erro ao geocodificar endereço:', error);
    return null;
  }
}

export const RouteModal = React.memo(function RouteModal({ isOpen, routeData, loading, error, onClose }) {
    const [unroutedOrders, setUnroutedOrders] = React.useState([]);
    const [routedOrders, setRoutedOrders] = React.useState([]);
    const [draggedOrder, setDraggedOrder] = React.useState(null);
    const [tooltipOrder, setTooltipOrder] = React.useState(null);
    const [showTooltip, setShowTooltip] = React.useState(false);
    const [tooltipPosition, setTooltipPosition] = React.useState({ x: 0, y: 0 });
    const [showClientGrouping, setShowClientGrouping] = React.useState(false);
    const [routeDateFilter, setRouteDateFilter] = React.useState('amanha'); // 'amanha' ou 'hoje'
    const [shareBusy, setShareBusy] = React.useState(false);
    const shareCacheRef = React.useRef({ amanha: null, hoje: null });
    
    // Estados para geocodificação e cache
    const [coordinatesCache, setCoordinatesCache] = React.useState({});
    const [loadingCoordinates, setLoadingCoordinates] = React.useState({});
    const [coordinatesError, setCoordinatesError] = React.useState({});
    const [isLoadingAllCoordinates, setIsLoadingAllCoordinates] = React.useState(false);
    const [isRetryingFailedAddresses, setIsRetryingFailedAddresses] = React.useState(false);
    const [retryResultMessage, setRetryResultMessage] = React.useState(null);
    
    // Estados para o mapa de ordens não roteirizadas
    const [showUnroutedMap, setShowUnroutedMap] = React.useState(false);
    const [sidebarExpanded, setSidebarExpanded] = React.useState(true);
    
    // Estados para roteirização no mapa
    const [selectedMapOrders, setSelectedMapOrders] = React.useState([]);
    const [mapRoutedOrders, setMapRoutedOrders] = React.useState([]);
    const [mapRoutedSequence, setMapRoutedSequence] = React.useState(1);
    const [savingRoute, setSavingRoute] = React.useState(false);
    const [saveProgress, setSaveProgress] = React.useState({ current: 0, total: 0 });
    const [routeDirty, setRouteDirty] = React.useState(false);
    const [savingBatch, setSavingBatch] = React.useState(false);
    const baselineRoutedOsRef = React.useRef([]);
    
    // Função para validar se as coordenadas estão no Brasil
    const isValidBrazilianCoordinates = (lat, lng) => {
      // Aproximadamente os limites do Brasil
      return lat >= -33.7683 && lat <= 5.2717 && lng >= -73.9872 && lng <= -34.7299;
    };
    const [mapTooltipOrder, setMapTooltipOrder] = React.useState(null);
    const [mapTooltipPosition, setMapTooltipPosition] = React.useState({ x: 0, y: 0 });
    const [routeDefeitoHover, setRouteDefeitoHover] = React.useState(null);

    const getRouteOrderClienteDisplay = (order) => {
      if (!order) return 'N/A';
      const fant = order.TB01008_FANTASIA ?? order.tb01008_fantasia;
      const nome = order.TB01008_NOME ?? order.tb01008_nome;
      const fStr = fant != null && fant !== '' ? String(fant).trim() : '';
      if (fStr) return fStr;
      const nStr = nome != null && nome !== '' ? String(nome).trim() : '';
      return nStr || 'N/A';
    };

    // Funções para gerenciar roteirização no mapa
    const addOrderToMapRoute = (order) => {
      // Se a ordem tem grupo de localização, adicionar todas as ordens do grupo
      if (order._locationGroup && order._locationGroup.length > 1) {
        console.log(`🗺️ Adicionando ${order._locationGroup.length} ordens do grupo à rota`);
        
        const ordersToAdd = [];
        let currentSequence = mapRoutedSequence;
        
        order._locationGroup.forEach(groupOrder => {
          const groupOrderId = groupOrder.id || groupOrder.TB02115_CODIGO;
          
          // Verificar se a ordem já está na rota
          const isAlreadyRouted = mapRoutedOrders.find(routed => 
            (routed.id || routed.TB02115_CODIGO) === groupOrderId
          );
          
          if (!isAlreadyRouted) {
            // Adicionar ordem à lista de ordens para adicionar
            const newRoutedOrder = {
              ...groupOrder,
              routeSequence: currentSequence,
              routeOrder: currentSequence
            };
            
            ordersToAdd.push(newRoutedOrder);
            currentSequence++;
          }
        });
        
        if (ordersToAdd.length > 0) {
          setMapRoutedOrders(prev => [...prev, ...ordersToAdd]);
          setMapRoutedSequence(currentSequence);
          
          // Marcar todas como selecionadas
          const orderIds = ordersToAdd.map(order => order.id || order.TB02115_CODIGO);
          setSelectedMapOrders(prev => [...prev, ...orderIds]);
        }
      } else {
        // Adicionar ordem individual
        const orderId = order.id || order.TB02115_CODIGO;
        
        // Verificar se a ordem já está na rota
        const isAlreadyRouted = mapRoutedOrders.find(routed => 
          (routed.id || routed.TB02115_CODIGO) === orderId
        );
        
        if (isAlreadyRouted) {
          return; // Ordem já está na rota
        }
        
        // Adicionar ordem à rota com sequência
        const newRoutedOrder = {
          ...order,
          routeSequence: mapRoutedSequence,
          routeOrder: mapRoutedSequence
        };
        
        setMapRoutedOrders(prev => [...prev, newRoutedOrder]);
        setMapRoutedSequence(prev => prev + 1);
        
        // Marcar como selecionada
        setSelectedMapOrders(prev => [...prev, orderId]);
      }
    };

    const removeOrderFromMapRoute = (order) => {
      const orderId = order.id || order.TB02115_CODIGO;
      
      // Se a ordem tem grupo de localização, remover todas as ordens do grupo
      if (order._locationGroup && order._locationGroup.length > 1) {
        console.log(`🗺️ Removendo ${order._locationGroup.length} ordens do grupo da rota`);
        
        const groupOrderIds = order._locationGroup.map(groupOrder => 
          groupOrder.id || groupOrder.TB02115_CODIGO
        );
        
        // Remover todas as ordens do grupo da rota
        setMapRoutedOrders(prev => prev.filter(routed => 
          !groupOrderIds.includes(routed.id || routed.TB02115_CODIGO)
        ));
        
        // Remover todas da seleção
        setSelectedMapOrders(prev => prev.filter(id => !groupOrderIds.includes(id)));
        
        // Reordenar sequência
        const remainingOrders = mapRoutedOrders.filter(routed => 
          !groupOrderIds.includes(routed.id || routed.TB02115_CODIGO)
        );
        
        const reorderedOrders = remainingOrders.map((order, index) => ({
          ...order,
          routeSequence: index + 1,
          routeOrder: index + 1
        }));
        
        setMapRoutedOrders(reorderedOrders);
        setMapRoutedSequence(reorderedOrders.length + 1);
      } else {
        // Remover ordem individual
        setMapRoutedOrders(prev => prev.filter(routed => 
          (routed.id || routed.TB02115_CODIGO) !== orderId
        ));
        
        // Remover da seleção
        setSelectedMapOrders(prev => prev.filter(id => id !== orderId));
        
        // Reordenar sequência (manter continuidade com ordens já roteirizadas)
        const remainingOrders = mapRoutedOrders.filter(routed => 
          (routed.id || routed.TB02115_CODIGO) !== orderId
        );
        
        const reorderedOrders = remainingOrders.map((order, index) => ({
          ...order,
          routeSequence: routedOrders.length + index + 1,
          routeOrder: routedOrders.length + index + 1
        }));
        
        setMapRoutedOrders(reorderedOrders);
        setMapRoutedSequence(routedOrders.length + reorderedOrders.length + 1);
      }
    };

    const isOrderInMapRoute = (order) => {
      const orderId = order.id || order.TB02115_CODIGO;
      
      // Se a ordem tem grupo de localização, verificar se alguma ordem do grupo está na rota
      if (order._locationGroup && order._locationGroup.length > 1) {
        return order._locationGroup.some(groupOrder => {
          const groupOrderId = groupOrder.id || groupOrder.TB02115_CODIGO;
          return mapRoutedOrders.find(routed => 
            (routed.id || routed.TB02115_CODIGO) === groupOrderId
          );
        });
      } else {
        // Verificar ordem individual
        return mapRoutedOrders.find(routed => 
          (routed.id || routed.TB02115_CODIGO) === orderId
        );
      }
    };

    // Função para remover ordem já roteirizada (via mapa) — só estado local (Salvar persiste)
    const removeRoutedOrderFromMap = (order) => {
      const orderId = order.id || order.TB02115_CODIGO;
      console.log(`🗑️ Removendo ordem ${orderId} da roteirização (rascunho local)...`);

      setUnroutedOrders((prev) => [...prev, { ...order, isRouted: false, routeSequence: null, routeOrder: null }]);
      setRoutedOrders((prev) => {
        const filtered = prev.filter(
          (routedOrder) => (routedOrder.id || routedOrder.TB02115_CODIGO) !== orderId
        );
        return filtered.map((o, index) => ({
          ...o,
          routeOrder: index + 1,
          routeSequence: index + 1,
        }));
      });
      setRouteDirty(true);

      if (mapTooltipOrder && (mapTooltipOrder.id || mapTooltipOrder.TB02115_CODIGO) === orderId) {
        setMapTooltipOrder(null);
      }
    };

    const getMapRouteProgress = () => {
      const totalAvailable = unroutedOrders.length;
      const totalRouted = mapRoutedOrders.length;
      const totalAlreadyRouted = routedOrders.length;
      const remaining = totalAvailable - totalRouted;
      
      return {
        total: totalAvailable,
        routed: totalRouted,
        alreadyRouted: totalAlreadyRouted,
        remaining: remaining,
        percentage: totalAvailable > 0 ? Math.round((totalRouted / totalAvailable) * 100) : 0
      };
    };

    // Função auxiliar para calcular data baseada no filtro
    const getForecastDateByFilter = () => {
      if (routeDateFilter === 'amanha') {
        const getRouteForecastDate = (date = new Date()) => {
          const currentDay = date.getDay();
          const year = date.getFullYear();
          const month = date.getMonth();
          const day = date.getDate();
          
          let forecastDate;
          if (currentDay >= 1 && currentDay <= 4) {
            forecastDate = new Date(year, month, day + 1);
          } else {
            const daysUntilMonday = currentDay === 0 ? 1 : (8 - currentDay);
            forecastDate = new Date(year, month, day + daysUntilMonday);
          }
          return forecastDate;
        };
        const forecastDateObj = getRouteForecastDate();
        return `${forecastDateObj.getFullYear()}-${String(forecastDateObj.getMonth() + 1).padStart(2, '0')}-${String(forecastDateObj.getDate()).padStart(2, '0')}`;
      } else {
        const today = new Date();
        return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      }
    };

    // Funções para mapear tipo de serviço e cores (consistentes com o resto da aplicação)
    const getServiceTypeFromPreventiva = (preventiva) => {
      const typeMap = {
        'E': 'E', // ESTOQUE
        'B': 'B', // BALCÃO
        'A': 'A', // AFERIÇÃO
        'R': 'R', // RETORNO-RECARGA
        'D': 'D', // DESINSTALAÇÃO
        'I': 'I', // INSTALAÇÃO
        'S': 'S', // PREVENTIVA
        'N': 'C'  // NORMAL/CORRETIVA - mapear para 'C' visualmente
      };
      return typeMap[preventiva] || 'C'; // Padrão para corretiva (C visualmente)
    };

    const getServiceColor = (tipo) => {
      switch(tipo) {
        case 'E': return '#9c27b0'; // ESTOQUE - Roxo
        case 'B': return '#ff5722'; // BALCÃO - Laranja
        case 'A': return '#607d8b'; // AFERIÇÃO - Azul acinzentado
        case 'R': return '#795548'; // RETORNO-RECARGA - Marrom
        case 'D': return '#f44336'; // DESINSTALAÇÃO - Vermelho
        case 'I': return '#2196f3'; // INSTALAÇÃO - Azul
        case 'S': return '#4caf50'; // PREVENTIVA - Verde
        case 'C': return '#ff9800'; // CORRETIVA - Amarelo/Laranja
        default: return '#9e9e9e';  // Cinza para desconhecido
      }
    };

    const renderRouteTipoBadge = (order) => {
      const defeitoAlert = hasDefeitoAlert(order);
      const defeitoBg = normalizeHexColor(order.color_bg);
      const tipoVisual = getServiceTypeFromPreventiva(order.TB02115_PREVENTIVA || 'N');
      return (
        <span
          className={`service-type-badge-compact${
            defeitoAlert ? ' service-type-badge-compact--defeito-alert board-cell-tooltip-trigger' : ''
          }`}
          style={{
            backgroundColor: defeitoBg || getServiceColor(tipoVisual),
          }}
          onMouseEnter={
            defeitoAlert
              ? (e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setRouteDefeitoHover({
                    text: defeitoAlertTooltipText(order),
                    x: r.left + r.width / 2,
                    y: r.bottom - 4,
                  });
                }
              : undefined
          }
          onMouseLeave={defeitoAlert ? () => setRouteDefeitoHover(null) : undefined}
        >
          {defeitoAlert ? strTrim(order.defeito_alert) : tipoVisual}
        </span>
      );
    };

    React.useEffect(() => {
      if (!isOpen) setRouteDefeitoHover(null);
    }, [isOpen]);

    React.useEffect(() => {
      if (routeData && routeData.orders) {
        // Separar ordens roteirizadas e não roteirizadas
        const unrouted = [];
        const routed = [];
        
        routeData.orders.forEach(order => {
          if (order.isRouted && order.routeSequence) {
            routed.push({
              ...order,
              routeOrder: order.routeSequence
            });
          } else {
            unrouted.push(order);
          }
        });
        
        // Ordenar ordens roteirizadas por sequência
        routed.sort((a, b) => a.routeOrder - b.routeOrder);
        
        setUnroutedOrders(unrouted);
        setRoutedOrders(routed);
        baselineRoutedOsRef.current = routed.map((o) => String(o.id || o.TB02115_CODIGO));
        setRouteDirty(false);
      }
    }, [routeData]);

    const orderOsCode = (order) => String(order?.id || order?.TB02115_CODIGO || '').trim();

    const handleSaveRouteBatch = React.useCallback(async () => {
      if (!routeData?.technicianId || !routeDirty || savingBatch) return;
      const forecastDate = getForecastDateByFilter();
      const items = routedOrders.map((order, index) => ({
        os: orderOsCode(order),
        sequence: index + 1,
      })).filter((it) => it.os);
      const currentSet = new Set(items.map((it) => it.os));
      const removedOs = (baselineRoutedOsRef.current || []).filter((os) => os && !currentSet.has(os));
      setSavingBatch(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}/api/route/save-batch`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            technicianId: routeData.technicianId,
            forecast: forecastDate,
            source: 'route_modal',
            items,
            removedOs,
          }),
        });
        const result = await response.json();
        if (!result.success) {
          throw new Error(result.message || 'Erro ao salvar roteiro');
        }
        baselineRoutedOsRef.current = items.map((it) => it.os);
        setRouteDirty(false);
        setRoutedOrders((prev) =>
          prev.map((o, i) => ({ ...o, routeOrder: i + 1, routeSequence: i + 1, isRouted: true }))
        );
      } catch (err) {
        console.error('❌ Erro ao salvar roteiro (batch):', err);
        window.alert(err.message || 'Erro ao salvar roteiro');
      } finally {
        setSavingBatch(false);
      }
    }, [routeData, routeDirty, savingBatch, routedOrders]);

    const requestCloseRouteModal = React.useCallback(() => {
      if (routeDirty) {
        const ok = window.confirm('Há alterações não salvas no roteiro. Deseja descartar?');
        if (!ok) return;
      }
      onClose?.();
    }, [routeDirty, onClose]);

    // Função para recarregar ordens baseado no filtro de data
    const reloadOrdersByFilter = React.useCallback(async () => {
      if (!routeData?.technicianId || !isOpen) return;
      
      try {
        setIsLoadingAllCoordinates(true);
        const technicianId = routeData.technicianId;
        const statusType = routeDateFilter === 'amanha' ? 'tomorrow' : 'today';
        
        // Calcular data baseada no filtro
        let targetDate;
        if (routeDateFilter === 'amanha') {
          const getRouteForecastDate = (date = new Date()) => {
            const currentDay = date.getDay();
            const year = date.getFullYear();
            const month = date.getMonth();
            const day = date.getDate();
            
            let forecastDate;
            if (currentDay >= 1 && currentDay <= 4) {
              forecastDate = new Date(year, month, day + 1);
            } else {
              const daysUntilMonday = currentDay === 0 ? 1 : (8 - currentDay);
              forecastDate = new Date(year, month, day + daysUntilMonday);
            }
            return forecastDate;
          };
          const forecastDateObj = getRouteForecastDate();
          targetDate = `${forecastDateObj.getFullYear()}-${String(forecastDateObj.getMonth() + 1).padStart(2, '0')}-${String(forecastDateObj.getDate()).padStart(2, '0')}`;
        } else {
          const today = new Date();
          targetDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        }
        
        // Buscar ordens do backend
        const token = localStorage.getItem('authToken');
        const apiUrl = `${API_BASE_URL}/api/orders/route/${technicianId}?date=${targetDate}&statusType=${statusType}`;
        
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const result = await response.json();
        
        if (result.success && result.data) {
          // Buscar ordens roteirizadas
          let routedOrdersFromDb = [];
          try {
            const routedResponse = await fetch(`${API_BASE_URL}/api/route/technician-orders/${technicianId}?forecast=${targetDate}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            const routedResult = await routedResponse.json();
            if (routedResult.success) {
              routedOrdersFromDb = routedResult.data.orders || [];
            }
          } catch (error) {
            console.warn('⚠️ Erro ao carregar ordens roteirizadas:', error);
          }
          
          // Processar ordens
          const processedOrders = result.data.map(order => {
            const orderNumber = String(order.TB02115_CODIGO);
            const routedOrder = routedOrdersFromDb.find(ro => String(ro.os) === orderNumber);
            
            const cidade = order.TB02115_CIDADE;
            const endereco = order.ENDERECO;
            let enderecoCompleto = endereco;
            
            if (!endereco) {
              enderecoCompleto = cidade;
            } else if (cidade && !endereco.toUpperCase().includes(cidade.toUpperCase())) {
              enderecoCompleto = `${endereco}, ${cidade}`;
            }
            
            // Usar o campo "Tipo" da query para determinar se está roteirizado
            const tipo = order.Tipo || '';
            const isRoutedByType = tipo === 'Roteirizado';
            
            const formatPrevisao = (d) => {
              if (!d) return null;
              try {
                const date = new Date(d);
                if (isNaN(date.getTime())) return null;
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const year = date.getFullYear();
                const hours = date.getHours().toString().padStart(2, '0');
                const minutes = date.getMinutes().toString().padStart(2, '0');
                return `${day}/${month}/${year} ${hours}:${minutes}`;
              } catch (e) { return null; }
            };
            return {
              ...order,
              id: order.TB02115_CODIGO,
              endereco: enderecoCompleto,
              isRouted: isRoutedByType || !!routedOrder,
              routeSequence: routedOrder?.sequence || order.TB02115_ORDEM || null,
              TB02115_NUMSERIE: order.TB02115_NUMSERIE || null,
              CALC_PREVISAO: order.CALC_PREVISAO || null,
              previsao: formatPrevisao(order.CALC_PREVISAO) || order.previsao || null,
              Tipo: tipo
            };
          });
          
          // Separar ordens roteirizadas e não roteirizadas baseado no campo "Tipo"
          const unrouted = [];
          const routed = [];
          
          processedOrders.forEach(order => {
            const tipo = order.Tipo || '';
            
            // Se o tipo for "Roteirizado", vai para roteirizadas
            if (tipo === 'Roteirizado') {
              routed.push({
                ...order,
                routeOrder: order.routeSequence || order.TB02115_ORDEM || 0
              });
            } 
            // Se o tipo for "Não Roteirizado" ou "Em aberto - Fora do roteiro", vai para não roteirizadas
            else if (tipo === 'Não Roteirizado' || tipo === 'Em aberto - Fora do roteiro') {
              unrouted.push(order);
            }
            // Fallback: usar lógica antiga se tipo não estiver definido
            else if (order.isRouted && order.routeSequence) {
              routed.push({
                ...order,
                routeOrder: order.routeSequence
              });
            } else {
              unrouted.push(order);
            }
          });
          
          routed.sort((a, b) => a.routeOrder - b.routeOrder);
          
          setUnroutedOrders(unrouted);
          setRoutedOrders(routed);
          baselineRoutedOsRef.current = routed.map((o) => String(o.id || o.TB02115_CODIGO));
          setRouteDirty(false);
          
          // Limpar cache de coordenadas e reprocessar
          setCoordinatesCache({});
          setLoadingCoordinates({});
          setCoordinatesError({});
          geocodingExecutedRef.current = false;
          
          // Reprocessar geolocalizações será feito pelo useEffect que monitora unroutedOrders
        } else {
          console.error('❌ Erro ao recarregar ordens:', result.message);
        }
      } catch (error) {
        console.error('❌ Erro ao recarregar ordens:', error);
      } finally {
        setIsLoadingAllCoordinates(false);
      }
    }, [routeData, routeDateFilter, isOpen]);

    // Restaurar "Para amanhã" com a mesma estrutura da abertura: routeData.orders + roteirizadas da API (sem API de roteiro)
    const restoreOrdersForAmanha = React.useCallback(async () => {
      if (!routeData?.technicianId || !routeData?.orders?.length || !isOpen) return;
      try {
        setIsLoadingAllCoordinates(true);
        const technicianId = routeData.technicianId;
        const getRouteForecastDate = (date = new Date()) => {
          const currentDay = date.getDay();
          const year = date.getFullYear();
          const month = date.getMonth();
          const day = date.getDate();
          if (currentDay >= 1 && currentDay <= 4) {
            return new Date(year, month, day + 1);
          }
          const daysUntilMonday = currentDay === 0 ? 1 : (8 - currentDay);
          return new Date(year, month, day + daysUntilMonday);
        };
        const forecastDateObj = getRouteForecastDate();
        const forecastDate = `${forecastDateObj.getFullYear()}-${String(forecastDateObj.getMonth() + 1).padStart(2, '0')}-${String(forecastDateObj.getDate()).padStart(2, '0')}`;
        let routedOrdersFromDb = [];
        try {
          const token = localStorage.getItem('authToken');
          const routedResponse = await fetch(`${API_BASE_URL}/api/route/technician-orders/${technicianId}?forecast=${forecastDate}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const routedResult = await routedResponse.json();
          if (routedResult.success) {
            routedOrdersFromDb = routedResult.data.orders || [];
          }
        } catch (err) {
          console.warn('⚠️ Erro ao carregar roteirizadas (Para amanhã):', err);
        }
        const ordersWithRouted = routeData.orders.map(order => {
          const orderNumber = String(order.id || order.TB02115_CODIGO);
          const routedOrder = routedOrdersFromDb.find(ro => String(ro.os) === orderNumber);
          return {
            ...order,
            isRouted: !!routedOrder,
            routeSequence: routedOrder?.sequence ?? order.routeSequence ?? null
          };
        });
        const unrouted = [];
        const routed = [];
        ordersWithRouted.forEach(order => {
          if (order.isRouted && order.routeSequence != null) {
            routed.push({ ...order, routeOrder: order.routeSequence });
          } else {
            unrouted.push(order);
          }
        });
        routed.sort((a, b) => a.routeOrder - b.routeOrder);
        setUnroutedOrders(unrouted);
        setRoutedOrders(routed);
        setCoordinatesCache({});
        setLoadingCoordinates({});
        setCoordinatesError({});
        geocodingExecutedRef.current = false;
      } catch (error) {
        console.error('❌ Erro ao restaurar ordens Para amanhã:', error);
      } finally {
        setIsLoadingAllCoordinates(false);
      }
    }, [routeData, isOpen]);

    // Ref para valor anterior do filtro: recarregar só quando o usuário trocar o filtro, não na abertura
    const previousFilterRef = React.useRef(null);
    
    // Recarregar ordens somente quando o usuário mudar o filtro (Para amanhã <-> Para hoje), nunca na abertura
    React.useEffect(() => {
      if (!isOpen) {
        previousFilterRef.current = null;
        return;
      }
      if (!routeData?.technicianId) return;
      const prev = previousFilterRef.current;
      previousFilterRef.current = routeDateFilter;
      if (prev !== null && prev !== routeDateFilter) {
        if (routeDateFilter === 'amanha') {
          restoreOrdersForAmanha();
        } else {
          reloadOrdersByFilter();
        }
      }
    }, [routeDateFilter, isOpen, routeData?.technicianId, restoreOrdersForAmanha, reloadOrdersByFilter]);

    // Ref para controlar se a geolocalização já foi executada quando o modal abriu
    const geocodingExecutedRef = React.useRef(false);

    // OTIMIZAÇÃO: Carregar coordenadas quando houver ordens não roteirizadas
    // E a geolocalização ainda não foi executada (inclui quando recarregar por filtro)
    React.useEffect(() => {
      // Resetar flag quando o modal fechar
      if (!isOpen) {
        geocodingExecutedRef.current = false;
        return;
      }
      
      // Executar quando houver ordens não roteirizadas e a geolocalização ainda não foi executada
      if (isOpen && unroutedOrders.length > 0 && !geocodingExecutedRef.current && !isLoadingAllCoordinates) {
        loadAllOrdersCoordinates();
        geocodingExecutedRef.current = true;
      }
    }, [isOpen, unroutedOrders.length, isLoadingAllCoordinates]);

    // Limpar roteirização quando o modal for fechado
    React.useEffect(() => {
      if (!isOpen) {
        setMapRoutedOrders([]);
        setSelectedMapOrders([]);
        setMapRoutedSequence(1);
        setMapTooltipOrder(null);
        setSavingRoute(false);
        setSaveProgress({ current: 0, total: 0 });
        
        // Limpar instância do mapa
        if (window.mapInstance) {
          window.mapInstance.remove();
          window.mapInstance = null;
        }
      }
    }, [isOpen]);

    const handleDragStart = (e, order) => {
      setDraggedOrder(order);
      e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    };

    const handleDropToRouted = async (e) => {
      e.preventDefault();
      if (draggedOrder) {
        const existingInRouted = routedOrders.find(order => 
          order.id === draggedOrder.id || order.TB02115_CODIGO === draggedOrder.TB02115_CODIGO
        );
        
        if (existingInRouted) {
          setDraggedOrder(null);
          return;
        }
        
        const newSequence = routedOrders.length + 1;
        const newRoutedOrder = {
          ...draggedOrder,
          routeOrder: newSequence,
          isRouted: true,
          routeSequence: newSequence,
          TB02115_NUMSERIE: draggedOrder.TB02115_NUMSERIE || draggedOrder.serie || draggedOrder.numeroSerie || null,
          CALC_PREVISAO: draggedOrder.CALC_PREVISAO || draggedOrder.previsao || null
        };
        
        setRoutedOrders([...routedOrders, newRoutedOrder]);
        setUnroutedOrders(unroutedOrders.filter(order => 
          order.id !== draggedOrder.id && order.TB02115_CODIGO !== draggedOrder.TB02115_CODIGO
        ));
        setRouteDirty(true);
        setDraggedOrder(null);
      }
    };
    const handleDropToUnrouted = async (e) => {
      e.preventDefault();
      if (draggedOrder) {
        const existingInUnrouted = unroutedOrders.find(order => 
          order.id === draggedOrder.id || order.TB02115_CODIGO === draggedOrder.TB02115_CODIGO
        );
        
        if (existingInUnrouted) {
          setDraggedOrder(null);
          return;
        }
        
        const unroutedOrder = { 
          ...draggedOrder, 
          isRouted: false, 
          routeSequence: null,
          routeOrder: null
        };
        setUnroutedOrders([...unroutedOrders, unroutedOrder]);
        setRoutedOrders(prev => {
          const filtered = prev.filter(order => 
            order.id !== draggedOrder.id && order.TB02115_CODIGO !== draggedOrder.TB02115_CODIGO
          );
          return filtered.map((order, index) => ({
            ...order,
            routeOrder: index + 1,
            routeSequence: index + 1
          }));
        });
        setRouteDirty(true);
        setDraggedOrder(null);
      }
    };
    const handleReorderRouted = async (dragIndex, dropIndex) => {
      const newRoutedOrders = [...routedOrders];
      const draggedItem = newRoutedOrders[dragIndex];
      newRoutedOrders.splice(dragIndex, 1);
      newRoutedOrders.splice(dropIndex, 0, draggedItem);
      
      newRoutedOrders.forEach((order, index) => {
        order.routeOrder = index + 1;
        order.routeSequence = index + 1;
      });
      
      setRoutedOrders(newRoutedOrders);
      setRouteDirty(true);
    };
    const handleDragOverRouted = (e, dropIndex) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      
      // Adicionar indicador visual de onde será inserido
      const target = e.currentTarget;
      target.classList.add('drag-over');
    };

    const handleDragLeave = (e) => {
      e.currentTarget.classList.remove('drag-over');
    };

    const handleDropInRouted = (e, dropIndex) => {
      e.preventDefault();
      e.currentTarget.classList.remove('drag-over');
      
      if (draggedOrder) {
        // Se a ordem já está na lista roteirizada, reordenar
        const existingIndex = routedOrders.findIndex(order => 
          order.id === draggedOrder.id || order.TB02115_CODIGO === draggedOrder.TB02115_CODIGO
        );
        
        if (existingIndex !== -1) {
          handleReorderRouted(existingIndex, dropIndex);
        } else {
          // Se é uma nova ordem, adicionar na posição
          const newRoutedOrder = {
            ...draggedOrder,
            routeOrder: dropIndex + 1,
            // Garantir que os campos do banco sejam preservados
            TB02115_NUMSERIE: draggedOrder.TB02115_NUMSERIE || draggedOrder.serie || draggedOrder.numeroSerie || null,
            CALC_PREVISAO: draggedOrder.CALC_PREVISAO || draggedOrder.previsao || null
          };
          
          const newRoutedOrders = [...routedOrders];
          newRoutedOrders.splice(dropIndex, 0, newRoutedOrder);
          
          // Reordenar números
          newRoutedOrders.forEach((order, index) => {
            order.routeOrder = index + 1;
          });
          
          setRoutedOrders(newRoutedOrders);
          setUnroutedOrders(unroutedOrders.filter(order => 
            order.id !== draggedOrder.id && order.TB02115_CODIGO !== draggedOrder.TB02115_CODIGO
          ));
        }
        setDraggedOrder(null);
      }
    };

    const handleOrderClick = async (order, event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      
      // Debug: Log dos campos disponíveis na ordem
      console.log('🔍 Debug - Campos da ordem no tooltip:', {
        id: order.id || order.TB02115_CODIGO,
        numeroSerie: order.numeroSerie,
        motivoOS: order.motivoOS,
        serie: order.serie || order.TB02115_NUMSERIE,
        motivo: order.motivo || order.TB02115_NOME,
        TB02115_NUMSERIE: order.TB02115_NUMSERIE,
        TB02115_NOME: order.TB02115_NOME,
        allFields: Object.keys(order).filter(key => key.includes('SERIE') || key.includes('NOME') || key.includes('MOTIVO'))
      });
      
      setTooltipPosition({
        x: rect.right + 10,
        y: rect.top
      });
      setTooltipOrder(order);
      setShowTooltip(true);
      
      // Carregar coordenadas se ainda não estiverem no cache
      const orderId = order.id || order.TB02115_CODIGO;
      if (!coordinatesCache[orderId] && !loadingCoordinates[orderId]) {
        await loadOrderCoordinates(order);
      }
    };

    const closeTooltip = () => {
      setShowTooltip(false);
      setTooltipOrder(null);
    };

    const hasLinkedOrder = (order) => {
      return order.pedidoVinculado || order.TB02115_PEDIDO_VINCULADO;
    };

    // Função para carregar coordenadas de uma ordem específica
    const loadOrderCoordinates = async (order) => {
      const orderId = order.id || order.TB02115_CODIGO;
      const endereco = order.endereco || order.TB02115_ENDERECO;
      
      if (!endereco) {
        console.log(`⚠️ Ordem ${orderId} não possui endereço`);
        return null;
      }

      // Verificar se já está no cache
      if (coordinatesCache[orderId]) {
        console.log(`✅ Coordenadas da ordem ${orderId} já estão em cache`);
        return coordinatesCache[orderId];
      }

      // Verificar se já está carregando
      if (loadingCoordinates[orderId]) {
        console.log(`⏳ Coordenadas da ordem ${orderId} já estão carregando`);
        return null;
      }

      console.log(`🌍 Carregando coordenadas para ordem ${orderId}: ${endereco}`);
      
      // Marcar como carregando
      setLoadingCoordinates(prev => ({ ...prev, [orderId]: true }));
      setCoordinatesError(prev => ({ ...prev, [orderId]: null }));

      try {
        const coordinates = await geocodeAddress(endereco);
        
        if (coordinates) {
          // Salvar no cache
          setCoordinatesCache(prev => ({
            ...prev,
            [orderId]: coordinates
          }));
          
          return coordinates;
        } else {
          console.log(`❌ Não foi possível obter coordenadas para ordem ${orderId}`);
          setCoordinatesError(prev => ({
            ...prev,
            [orderId]: 'Endereço não encontrado'
          }));
          return null;
        }
      } catch (error) {
        console.error(`❌ Erro ao carregar coordenadas para ordem ${orderId}:`, error);
        setCoordinatesError(prev => ({
          ...prev,
          [orderId]: error.message
        }));
        return null;
      } finally {
        setLoadingCoordinates(prev => ({ ...prev, [orderId]: false }));
      }
    };

    // Função para carregar coordenadas de todas as ordens em lote
    const loadAllOrdersCoordinates = async () => {
      // Evitar carregamento duplicado
      if (isLoadingAllCoordinates) {
        return;
      }
      
      setIsLoadingAllCoordinates(true);
      
      try {
        // OTIMIZAÇÃO: Processar APENAS ordens não roteirizadas
        // Ordens roteirizadas não precisam de geolocalização (já foram processadas anteriormente)
        const ordersToProcess = [...unroutedOrders];
        
        if (!ordersToProcess || ordersToProcess.length === 0) {
          return;
        }
        
        // Preparar endereços para geocodificação em lote
        const addressesToGeocode = [];
        
        for (const order of ordersToProcess) {
          const orderId = order.id || order.TB02115_CODIGO;
          const endereco = order.endereco || order.TB02115_ENDERECO;
          
          if (endereco && !coordinatesCache[orderId] && !loadingCoordinates[orderId]) {
            addressesToGeocode.push({
              orderId,
              address: endereco
            });
          } else if (coordinatesCache[orderId]) {
            // já em cache
          } else if (!endereco) {
            // sem endereço
          }
        }
        
        if (addressesToGeocode.length === 0) {
          return;
        }
        
        const loadingFlags = {};
        const clearedErrors = {};
        addressesToGeocode.forEach(({ orderId }) => {
          loadingFlags[orderId] = true;
          clearedErrors[orderId] = null;
        });
        setLoadingCoordinates((prev) => ({ ...prev, ...loadingFlags }));
        setCoordinatesError((prev) => ({ ...prev, ...clearedErrors }));
        
        // Fazer requisição em lote para o backend
        const token = localStorage.getItem('authToken');
        
        // SOLUÇÃO DIRETA: Construir URL explicitamente
        let geocodeUrl;
        const h = window.location.hostname;
        if (h === 'localhost' || h === '127.0.0.1') {
          geocodeUrl = 'http://localhost:3002/api/geocode/batch';
        } else if (h === '177.102.48.21' || h === '45.168.203.209') {
          geocodeUrl = `http://${h}:3002/api/geocode/batch`;
        } else {
          // Para produção: usar mesma origem (Apache proxy encaminha /api)
          geocodeUrl = `${window.location.origin}/api/geocode/batch`;
        }
        
        const response = await fetch(geocodeUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            addresses: addressesToGeocode
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.success && data.data) {
          const nextCache = {};
          const nextErrors = {};
          data.data.forEach((result) => {
            const orderId = result.orderId;
            if (result.error) {
              nextErrors[orderId] = result.error;
            } else if (result.lat && result.lng) {
              nextCache[orderId] = {
                lat: result.lat,
                lng: result.lng,
                display_name: result.display_name || result.address,
              };
            }
          });
          if (Object.keys(nextErrors).length) {
            setCoordinatesError((prev) => ({ ...prev, ...nextErrors }));
          }
          if (Object.keys(nextCache).length) {
            setCoordinatesCache((prev) => ({ ...prev, ...nextCache }));
          }
        }
        
      } catch (error) {
        console.error('❌ Erro durante carregamento de coordenadas em lote:', error);
        
        // Marcar todos como erro
        const ordersToProcess = routeData?.orders || [...unroutedOrders, ...routedOrders];
        ordersToProcess.forEach(order => {
          const orderId = order.id || order.TB02115_CODIGO;
          if (loadingCoordinates[orderId]) {
            setCoordinatesError(prev => ({
              ...prev,
              [orderId]: 'Erro na geocodificação em lote'
            }));
          }
        });
      } finally {
        // Limpar estado de carregamento
        setLoadingCoordinates({});
        setIsLoadingAllCoordinates(false);
      }
    };

    // Função para refazer busca de endereços não encontrados
    const retryFailedAddresses = async () => {
      // Filtrar apenas ordens sem coordenadas
      const ordersWithoutCoordinates = unroutedOrders.filter(order => {
        const orderId = order.id || order.TB02115_CODIGO;
        return !coordinatesCache[orderId];
      });
      
      if (ordersWithoutCoordinates.length === 0) {
        console.log('⚠️ Nenhuma ordem sem coordenadas para refazer busca');
        return;
      }
      
      console.log(`🔄 Refazendo busca de ${ordersWithoutCoordinates.length} endereços não encontrados...`);
      setIsRetryingFailedAddresses(true);
      
      try {
        // Preparar endereços para geocodificação
        const addressesToGeocode = [];
        
        for (const order of ordersWithoutCoordinates) {
          const orderId = order.id || order.TB02115_CODIGO;
          const endereco = order.endereco || order.TB02115_ENDERECO;
          
          if (endereco) {
            addressesToGeocode.push({
              orderId,
              address: endereco
            });
            // Marcar como carregando
            setLoadingCoordinates(prev => ({ ...prev, [orderId]: true }));
            setCoordinatesError(prev => ({ ...prev, [orderId]: null }));
          }
        }
        
        if (addressesToGeocode.length === 0) {
          console.log('⚠️ Nenhum endereço válido para geocodificar');
          setIsRetryingFailedAddresses(false);
          return;
        }
        
        // Fazer requisição em lote para o backend
        const token = localStorage.getItem('authToken');
        
        let geocodeUrl;
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          geocodeUrl = 'http://localhost:3002/api/geocode/batch';
        } else {
          // Para produção: usar mesma origem (Apache proxy encaminha /api)
          geocodeUrl = `${window.location.origin}/api/geocode/batch`;
        }
        
        const response = await fetch(geocodeUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            addresses: addressesToGeocode
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`📍 Resultado da busca refeita:`, data);
        
        let foundCount = 0;
        let notFoundCount = 0;
        
        if (data.success && data.data) {
          const nextCache = {};
          const nextErrors = {};
          data.data.forEach((result) => {
            const orderId = result.orderId;
            if (result.error) {
              nextErrors[orderId] = result.error;
              notFoundCount++;
            } else if (result.lat && result.lng) {
              nextCache[orderId] = {
                lat: result.lat,
                lng: result.lng,
                display_name: result.display_name || result.address,
              };
              foundCount++;
            }
          });
          if (Object.keys(nextErrors).length) {
            setCoordinatesError((prev) => ({ ...prev, ...nextErrors }));
          }
          if (Object.keys(nextCache).length) {
            setCoordinatesCache((prev) => ({ ...prev, ...nextCache }));
          }
          
          // Atualizar o mapa se houver novas coordenadas encontradas
          if (foundCount > 0 && showUnroutedMap && window.mapInstance) {
            console.log(`🗺️ Atualizando mapa com ${foundCount} novas coordenadas encontradas`);
            // Aguardar um pouco para garantir que o cache foi atualizado
            setTimeout(() => {
              updateMapMarkers();
            }, 100);
          }
          
          // Mostrar mensagem ao usuário
          if (foundCount > 0 && notFoundCount === 0) {
            setRetryResultMessage({
              type: 'success',
              text: `${foundCount} endereço(s) encontrado(s) com sucesso!`
            });
            // As ordens encontradas automaticamente sairão da lista de "não encontradas"
            // pois agora têm coordenadas no cache
          } else if (foundCount > 0 && notFoundCount > 0) {
            setRetryResultMessage({
              type: 'partial',
              text: `${foundCount} endereço(s) encontrado(s), ${notFoundCount} ainda não encontrado(s)`
            });
          } else if (notFoundCount > 0) {
            setRetryResultMessage({
              type: 'error',
              text: `Não foi possível encontrar ${notFoundCount} endereço(s)`
            });
          }
          
          // Limpar mensagem após 5 segundos
          setTimeout(() => {
            setRetryResultMessage(null);
          }, 5000);
        }
        
      } catch (error) {
        console.error('❌ Erro ao refazer busca de endereços:', error);
        
        // Marcar todos como erro
        ordersWithoutCoordinates.forEach(order => {
          const orderId = order.id || order.TB02115_CODIGO;
          setCoordinatesError(prev => ({
            ...prev,
            [orderId]: 'Erro ao refazer busca'
          }));
        });
      } finally {
        // Limpar estado de carregamento
        setLoadingCoordinates(prev => {
          const newState = { ...prev };
          ordersWithoutCoordinates.forEach(order => {
            const orderId = order.id || order.TB02115_CODIGO;
            delete newState[orderId];
          });
          return newState;
        });
        setIsRetryingFailedAddresses(false);
      }
    };

    // Função para agrupar ordens por localização
    const groupOrdersByLocation = (orders) => {
      const groups = {};
      const tolerance = 0.001; // Aproximadamente 100 metros
      
      orders.forEach(order => {
        const orderId = order.id || order.TB02115_CODIGO;
        const coordinates = coordinatesCache[orderId];
        
        if (coordinates && 
            typeof coordinates.lat === 'number' && 
            typeof coordinates.lng === 'number' && 
            !isNaN(coordinates.lat) && 
            !isNaN(coordinates.lng)) {
          
          // Criar chave de localização arredondada
          const latKey = Math.round(coordinates.lat / tolerance) * tolerance;
          const lngKey = Math.round(coordinates.lng / tolerance) * tolerance;
          const locationKey = `${latKey},${lngKey}`;
          
          if (!groups[locationKey]) {
            groups[locationKey] = {
              coordinates: coordinates,
              orders: []
            };
          }
          
          groups[locationKey].orders.push(order);
        }
      });
      
      return groups;
    };

    /** Número no pin: sequência da rota (sessão do mapa ou já persistida); senão, quantidade de OS no local. */
    const getMapLocationMarkerLabel = (orders, hasMapRoutedOrders, hasAlreadyRoutedOrders) => {
      const orderIdStr = (o) => String(o.id || o.TB02115_CODIGO);
      if (hasMapRoutedOrders) {
        const seqs = [];
        orders.forEach((o) => {
          const r = mapRoutedOrders.find((ro) => orderIdStr(ro) === orderIdStr(o));
          if (r) {
            const n = r.routeOrder ?? r.routeSequence;
            if (n != null && !Number.isNaN(Number(n))) seqs.push(Number(n));
          }
        });
        if (seqs.length) return String(Math.min(...seqs));
      }
      if (hasAlreadyRoutedOrders) {
        const seqs = orders
          .filter((o) => o.isRouted && o.routeSequence != null)
          .map((o) => Number(o.routeSequence))
          .filter((n) => !Number.isNaN(n));
        if (seqs.length) return String(Math.min(...seqs));
      }
      return String(orders.length);
    };

    const isMapMarkerLabelWide = (label) => label.length > 1 || Number(label) > 9;

    // Função para inicializar o mapa de ordens não roteirizadas
    const initializeUnroutedMap = () => {
      if (!window.L) {
        console.error('❌ Leaflet não carregado');
        return;
      }

      const mapElement = document.getElementById('unrouted-map');
      if (!mapElement) {
        console.error('❌ Elemento do mapa não encontrado');
        return;
      }

      // Se o mapa já existe, apenas atualizar os marcadores
      if (window.mapInstance) {
        console.log('🔄 Mapa já existe, atualizando marcadores...');
        updateMapMarkers();
        return;
      }

      // Filtrar ordens com coordenadas disponíveis (incluindo roteirizadas e não roteirizadas)
      const allOrders = [...unroutedOrders, ...routedOrders];
      const ordersWithCoordinates = allOrders.filter(order => {
        const orderId = order.id || order.TB02115_CODIGO;
        return coordinatesCache[orderId];
      });

      if (ordersWithCoordinates.length === 0) {
        console.log('⚠️ Nenhuma ordem com coordenadas disponível para exibir no mapa');
        return;
      }

      // Agrupar ordens por localização
      const locationGroups = groupOrdersByLocation(ordersWithCoordinates);
      console.log(`🗺️ ${Object.keys(locationGroups).length} locais únicos encontrados`);

      // Calcular centro do mapa baseado nas coordenadas disponíveis
      const bounds = [];
      const markers = [];
      const brazilBounds = {
        north: -5.0,  // Norte (Roraima)
        south: -34.0, // Sul (Rio Grande do Sul)
        east: -34.0,  // Leste (Rio Grande do Norte)
        west: -74.0   // Oeste (Acre)
      };

      Object.entries(locationGroups).forEach(([locationKey, group]) => {
        const coordinates = group.coordinates;
        const orders = group.orders;
        
        if (coordinates && 
            typeof coordinates.lat === 'number' && 
            typeof coordinates.lng === 'number' && 
            !isNaN(coordinates.lat) && 
            !isNaN(coordinates.lng) &&
            coordinates.lat >= -90 && coordinates.lat <= 90 &&
            coordinates.lng >= -180 && coordinates.lng <= 180) {
          
          const position = [coordinates.lat, coordinates.lng];
          bounds.push(position);

          // Verificar se alguma ordem do grupo está na rota atual do mapa ou já foi roteirizada
          const hasMapRoutedOrders = orders.some(order => isOrderInMapRoute(order));
          const hasAlreadyRoutedOrders = orders.some(order => order.isRouted && order.routeSequence);
          

          
          // Definir cor do marcador baseado no status (priorizar ordens roteirizadas)
          let markerColor = '#ef4444'; // Vermelho para não roteirizadas
          if (hasMapRoutedOrders) {
            markerColor = '#10b981'; // Verde claro para ordens adicionadas na rota atual do mapa
          } else if (hasAlreadyRoutedOrders) {
            // Se há pelo menos uma ordem roteirizada no grupo, mostrar verde escuro
            markerColor = '#059669'; // Verde escuro para ordens já roteirizadas anteriormente
          }
          
          const markerLabel = getMapLocationMarkerLabel(orders, hasMapRoutedOrders, hasAlreadyRoutedOrders);
          const labelWide = isMapMarkerLabelWide(markerLabel);

          // Criar marcador personalizado (sequência da rota ou quantidade no local)
          const customIcon = window.L.divIcon({
            className: 'custom-marker',
            html: `
              <div style="
                width: ${labelWide ? '28px' : '24px'}; 
                height: 24px; 
                background: ${markerColor}; 
                border: 2px solid white; 
                border-radius: 50%; 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                color: white; 
                font-size: ${labelWide ? '9px' : '10px'}; 
                font-weight: bold;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                cursor: pointer;
              ">
                ${markerLabel}
              </div>
            `,
            iconSize: [labelWide ? 28 : 24, 24],
            iconAnchor: [labelWide ? 14 : 12, 12]
          });

          const marker = window.L.marker(position, { icon: customIcon });

          // Adicionar evento de clique no marcador
          marker.on('click', (event) => {
            const rect = event.originalEvent.target.getBoundingClientRect();
            setMapTooltipPosition({
              x: rect.left + rect.width / 2,
              y: rect.top - 10
            });
            // Mostrar primeira ordem do grupo no tooltip
            setMapTooltipOrder({
              ...orders[0],
              _locationGroup: orders // Adicionar todas as ordens do grupo
            });
          });

          markers.push(marker);
        } else {
          console.warn(`⚠️ Coordenadas inválidas para localização ${locationKey}:`, coordinates);
        }
      });

      // Criar mapa
      const map = window.L.map('unrouted-map').setView(
        bounds.length > 0 ? bounds[0] : [-23.5505, -46.6333], // São Paulo como fallback
        12
      );

      // Armazenar instância do mapa globalmente
      window.mapInstance = map;

      // Adicionar camada de tiles do OpenStreetMap
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      // Adicionar marcadores ao mapa
      markers.forEach(marker => {
        marker.addTo(map);
      });

      // Filtrar apenas coordenadas brasileiras para o foco do mapa
      const brazilianBounds = bounds.filter(position => {
        const [lat, lng] = position;
        return lat >= brazilBounds.south && lat <= brazilBounds.north &&
               lng >= brazilBounds.west && lng <= brazilBounds.east;
      });

      // Ajustar zoom para mostrar apenas marcadores brasileiros
      if (brazilianBounds.length > 1) {
        map.fitBounds(brazilianBounds);
      } else if (brazilianBounds.length === 1) {
        // Se há apenas um ponto, centralizar nele com zoom apropriado
        const [lat, lng] = brazilianBounds[0];
        map.setView([lat, lng], 12);
      } else {
        // Centro padrão do Brasil se não houver coordenadas brasileiras
        map.setView([-15.7801, -47.9292], 5);
      }

      console.log(`🗺️ Mapa OpenStreetMap inicializado com ${markers.length} marcadores`);
    };

    // Função para atualizar apenas os marcadores sem reinicializar o mapa
    const updateMapMarkers = () => {
      if (!window.mapInstance) {
        console.log('⚠️ Mapa não inicializado, pulando atualização de marcadores');
        return;
      }

      console.log('🔄 Atualizando marcadores do mapa');
      console.log(`📊 Estado atual: ${unroutedOrders.length} não roteirizadas, ${routedOrders.length} roteirizadas, ${mapRoutedOrders.length} na rota do mapa`);
      
      // Remover todos os marcadores existentes
      window.mapInstance.eachLayer((layer) => {
        if (layer instanceof window.L.Marker) {
          window.mapInstance.removeLayer(layer);
        }
      });

      // Verificar se há ordens com coordenadas (incluindo roteirizadas e não roteirizadas)
      const allOrders = [...unroutedOrders, ...routedOrders];
      const ordersWithCoordinates = allOrders.filter(order => {
        const orderId = order.id || order.TB02115_CODIGO;
        return coordinatesCache[orderId];
      });
      
      if (ordersWithCoordinates.length === 0) {
        console.log('⚠️ Nenhuma ordem com coordenadas disponível para atualizar marcadores');
        return;
      }
      
      // Agrupar ordens por localização
      const locationGroups = groupOrdersByLocation(ordersWithCoordinates);
      const bounds = [];
      
      // Limites do Brasil para filtro
      const brazilBounds = {
        north: -5.0,
        south: -34.0,
        east: -34.0,
        west: -74.0
      };
      
      Object.entries(locationGroups).forEach(([locationKey, group]) => {
        const coordinates = group.coordinates;
        const orders = group.orders;
        
        if (coordinates && 
            typeof coordinates.lat === 'number' && 
            typeof coordinates.lng === 'number' && 
            !isNaN(coordinates.lat) && 
            !isNaN(coordinates.lng) &&
            coordinates.lat >= -90 && coordinates.lat <= 90 &&
            coordinates.lng >= -180 && coordinates.lng <= 180) {
          
          const position = [coordinates.lat, coordinates.lng];
          bounds.push(position);

          // Verificar se alguma ordem do grupo está na rota atual do mapa ou já foi roteirizada
          const hasMapRoutedOrders = orders.some(order => isOrderInMapRoute(order));
          const hasAlreadyRoutedOrders = orders.some(order => order.isRouted && order.routeSequence);
          

          
          // Definir cor do marcador baseado no status (priorizar ordens roteirizadas)
          let markerColor = '#ef4444'; // Vermelho para não roteirizadas
          if (hasMapRoutedOrders) {
            markerColor = '#10b981'; // Verde claro para ordens adicionadas na rota atual do mapa
          } else if (hasAlreadyRoutedOrders) {
            // Se há pelo menos uma ordem roteirizada no grupo, mostrar verde escuro
            markerColor = '#059669'; // Verde escuro para ordens já roteirizadas anteriormente
          }
          
          const markerLabel = getMapLocationMarkerLabel(orders, hasMapRoutedOrders, hasAlreadyRoutedOrders);
          const labelWide = isMapMarkerLabelWide(markerLabel);

          // Criar marcador personalizado (sequência da rota ou quantidade no local)
          const customIcon = window.L.divIcon({
            className: 'custom-marker',
            html: `
              <div style="
                width: ${labelWide ? '28px' : '24px'}; 
                height: 24px; 
                background: ${markerColor}; 
                border: 2px solid white; 
                border-radius: 50%; 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                color: white; 
                font-size: ${labelWide ? '9px' : '10px'}; 
                font-weight: bold;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                cursor: pointer;
              ">
                ${markerLabel}
              </div>
            `,
            iconSize: [labelWide ? 28 : 24, 24],
            iconAnchor: [labelWide ? 14 : 12, 12]
          });

          const marker = window.L.marker(position, { icon: customIcon });

          // Adicionar evento de clique no marcador
          marker.on('click', (event) => {
            const rect = event.originalEvent.target.getBoundingClientRect();
            setMapTooltipPosition({
              x: rect.left + rect.width / 2,
              y: rect.top - 10
            });
            // Mostrar primeira ordem do grupo no tooltip
            setMapTooltipOrder({
              ...orders[0],
              _locationGroup: orders // Adicionar todas as ordens do grupo
            });
          });

          marker.addTo(window.mapInstance);
        } else {
          console.warn(`⚠️ Coordenadas inválidas para localização ${locationKey}:`, coordinates);
        }
      });

      console.log(`🔄 Marcadores atualizados: ${bounds.length} pontos`);
      
      // Ajustar visualização do mapa para mostrar todos os marcadores
      if (bounds.length > 0) {
        // Filtrar apenas coordenadas brasileiras para o foco do mapa
        const brazilianBounds = bounds.filter(position => {
          const [lat, lng] = position;
          return lat >= brazilBounds.south && lat <= brazilBounds.north &&
                 lng >= brazilBounds.west && lng <= brazilBounds.east;
        });
        
        if (brazilianBounds.length > 1) {
          // Criar um grupo de limites (LatLngBounds) para ajustar o zoom
          const boundsGroup = window.L.latLngBounds(brazilianBounds);
          window.mapInstance.fitBounds(boundsGroup, { padding: [50, 50] }); // padding de 50px em todas as direções
          console.log(`🗺️ Visualização do mapa ajustada para mostrar ${brazilianBounds.length} marcadores`);
        } else if (brazilianBounds.length === 1) {
          // Se há apenas um ponto, centralizar nele com zoom apropriado
          const [lat, lng] = brazilianBounds[0];
          window.mapInstance.setView([lat, lng], 12);
          console.log(`🗺️ Visualização do mapa centralizada no único marcador`);
        }
      }
    };

    // useEffect para inicializar o mapa quando o modal abrir
    React.useEffect(() => {
      if (showUnroutedMap) {
        // Aguardar um pouco para garantir que o DOM foi renderizado
        const timer = setTimeout(() => {
          initializeUnroutedMap();
        }, 100);
        
        return () => clearTimeout(timer);
      } else {
        // Limpar instância do mapa quando o modal for fechado
        if (window.mapInstance) {
          console.log('🧹 Limpando instância do mapa...');
          window.mapInstance.remove();
          window.mapInstance = null;
        }
      }
    }, [showUnroutedMap]);

    // useEffect para ajustar a sequência do mapa baseado nas ordens já roteirizadas
    React.useEffect(() => {
      if (showUnroutedMap) {
        // Calcular a próxima sequência baseada nas ordens já roteirizadas
        const nextSequence = routedOrders.length + 1;
        console.log(`🔢 Ajustando sequência do mapa para: ${nextSequence} (baseado em ${routedOrders.length} ordens já roteirizadas)`);
        setMapRoutedSequence(nextSequence);
      }
    }, [showUnroutedMap, routedOrders.length]);

    // useEffect separado para atualizar marcadores quando a roteirização mudar
    React.useEffect(() => {
      if (showUnroutedMap && window.mapInstance) {
        // Atualizar apenas os marcadores existentes sem reinicializar o mapa
        updateMapMarkers();
      }
    }, [mapRoutedOrders, routedOrders, unroutedOrders, coordinatesCache]);

    // Função para imprimir o roteiro
    // Função para formatar data sem problemas de fuso horário (usa data local)
    const formatDateLocal = (date) => {
      if (!date) return '';
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };

    // Função para formatar data e hora sem problemas de fuso horário (usa data/hora local)
    const formatDateTimeLocal = () => {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    // Função para formatar data/hora de previsão
    const formatDateTime = (dateTimeString) => {
      if (!dateTimeString) return 'N/A';
      // Se já está formatado como string (ex: "21/05/2025 14:37"), retornar diretamente
      if (typeof dateTimeString === 'string' && dateTimeString.includes('/') && dateTimeString.includes(':')) {
        return dateTimeString;
      }
      try {
        const date = new Date(dateTimeString);
        if (isNaN(date.getTime())) return 'N/A';
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        return 'N/A';
      }
    };

    // Função para obter equipamento com fallback
    const getEquipment = (order) => {
      return order.TB01010_RESUMIDO || order.TB01010_NOME || order.equipamento || 'N/A';
    };

    const printRoute = async () => {
      if (routedOrders.length === 0) {
        alert('Não há ordens roteirizadas para imprimir.');
        return;
      }

      const escHtml = escapeRoutePrintHtml;

      // Ordenar dados por routeOrder (crescente)
      const sortedData = [...routedOrders].sort((a, b) => {
        const orderA = a.routeOrder || a.routeSequence || 0;
        const orderB = b.routeOrder || b.routeSequence || 0;
        return orderA - orderB;
      });

      const token = localStorage.getItem('authToken');
      const printData = await enrichRouteOrdersDefeitoObs(sortedData, API_BASE_URL, token);

      // Criar conteúdo HTML do relatório
      const today = routeData?.forecastDate ? new Date(routeData.forecastDate) : new Date();
      const reportDate = formatDateLocal(today);
      const reportDateTime = formatDateTimeLocal();

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Roteiro de Atendimento Técnico</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 1.5cm;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Geist Variable', 'Geist', system-ui, sans-serif;
              font-size: 10px;
              color: #2d3748;
              line-height: 1.3;
            }
            .report-header {
              text-align: left;
              margin-bottom: 10px;
            }
            .report-header h1 {
              font-size: 16px;
              font-weight: 600;
              color: #2d3748;
              margin-bottom: 8px;
            }
            .report-technician {
              text-align: left;
              font-size: 13px;
              font-weight: 500;
              color: #4a5568;
              margin-bottom: 12px;
              padding-bottom: 12px;
              border-bottom: 2px solid #7c4dff;
            }
            .report-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 15px;
              table-layout: fixed;
            }
            .report-table thead {
              background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
              border-bottom: 2px solid #e2e8f0;
            }
            .report-table th {
              padding: 8px 6px;
              text-align: left;
              font-weight: 600;
              color: #2d3748;
              font-size: 9px;
              text-transform: uppercase;
              letter-spacing: 0.3px;
              border-bottom: 2px solid #e2e8f0;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .report-table td {
              padding: 6px;
              border-bottom: 1px solid #f1f5f9;
              font-size: 9px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            .report-table tbody tr:hover {
              background-color: #f8fafc;
            }
            .report-total {
              margin-top: 12px;
              padding-top: 8px;
              border-top: 2px solid #e2e8f0;
              font-weight: 600;
              font-size: 10px;
              color: #2d3748;
            }
            .report-footer {
              margin-top: 20px;
              padding-top: 8px;
              border-top: 1px solid #e2e8f0;
              text-align: right;
              font-size: 8px;
              color: #64748b;
            }
            .col-number { width: 4%; }
            .col-os { width: 5%; }
            .col-pedido { width: 5%; }
            .col-cliente { width: 17%; }
            .col-equipamento { width: 23%; }
            .col-serie { width: 7%; }
            .col-patrimonio { width: 7%; }
            .col-cidade { width: 11%; }
            .col-bairro { width: 11%; }
            .col-previsao { width: 10%; }
            ${ROUTE_PRINT_DEFEITO_CSS}
          </style>
        </head>
        <body>
          <div class="report-header">
            <h1>Roteiro de atendimento técnico - ${reportDate}</h1>
            <div class="report-technician">
              ${routeData?.technicianName || 'N/A'}
            </div>
          </div>
          <table class="report-table">
            <thead>
              <tr>
                <th class="col-number">#</th>
                <th class="col-os">OS</th>
                <th class="col-pedido">PEDIDO</th>
                <th class="col-cliente">Cliente</th>
                <th class="col-equipamento">Equipamento</th>
                <th class="col-serie">Série</th>
                <th class="col-patrimonio">Patrimônio</th>
                <th class="col-cidade">Cidade</th>
                <th class="col-bairro">Bairro</th>
                <th class="col-previsao">Previsão</th>
              </tr>
            </thead>
            <tbody>
              ${printData.map((ordem) => {
                const orderNumber = ordem.routeOrder || ordem.routeSequence || '-';
                // Garantir que os campos do banco sejam verificados primeiro
                // Para série, verificar múltiplos campos possíveis
                const serie = ordem.TB02115_NUMSERIE || ordem.serie || ordem.numeroSerie || ordem.TB02115_SERIE || 'N/A';
                // Para previsão, priorizar previsao (já formatado) quando CALC_PREVISAO não estiver disponível
                const previsao = ordem.previsao || ordem.CALC_PREVISAO || null;
                return `
                  <tr>
                    <td>${escHtml(orderNumber)}</td>
                    <td>${escHtml(ordem.id || ordem.TB02115_CODIGO || 'N/A')}</td>
                    <td>${escHtml(ordem.PEDIDO_VINCULADO || ordem.pedidoVinculado || ordem.TB02115_PEDIDO_VINCULADO || 'N/A')}</td>
                    <td>${escHtml(ordem.cliente || ordem.TB01008_NOME || 'N/A')}</td>
                    <td>${escHtml(getEquipment(ordem))}</td>
                    <td>${escHtml(serie)}</td>
                    <td>${escHtml(ordem.patrimonio || ordem.TB02112_PAT || 'N/A')}</td>
                    <td>${escHtml(ordem.cidade || ordem.TB02115_CIDADE || 'N/A')}</td>
                    <td>${escHtml(ordem.bairro || ordem.TB02115_BAIRRO || 'N/A')}</td>
                    <td>${escHtml(formatDateTime(previsao))}</td>
                  </tr>
                  ${buildRouteDefeitoPrintRows(ordem, escHtml)}
                `;
              }).join('')}
            </tbody>
          </table>
          <div class="report-total">
            Total de ordens de serviço: ${printData.length}
          </div>
          <div class="report-footer">
            Relatório emitido em ${reportDateTime}
          </div>
        </body>
        </html>
      `;

      // Criar janela de impressão
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Aguardar carregamento e imprimir
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };
    };

    const shareRoute = async () => {
      if (routedOrders.length === 0 || shareBusy) return;
      const technicianId = routeData?.technicianId;
      if (!technicianId) {
        alert('Técnico não identificado para compartilhar o roteiro.');
        return;
      }
      const day = routeDateFilter === 'hoje' ? 'hoje' : 'amanha';
      const osSequence = routedOrders
        .map((o) => String(o?.TB02115_CODIGO || o?.id || o?.os || '').trim())
        .filter(Boolean);
      if (!osSequence.length) {
        alert('Não há ordens roteirizadas para compartilhar.');
        return;
      }
      const forecast = routeData?.forecastDate || '';
      const fingerprint = routeShareFingerprint(day, forecast, osSequence);
      const cached = shareCacheRef.current?.[day];
      const openPanel = (payload) => {
        openRoadmapSharePanel({
          ...payload,
          technicianName: routeData?.technicianName || '',
          onPrint: () => {
            printRoute();
          },
        });
      };
      if (cached?.url && cached.fingerprint === fingerprint) {
        openPanel({
          url: cached.url,
          reused: true,
          validFrom: cached.validFrom,
          expiresAt: cached.expiresAt,
        });
        return;
      }
      setShareBusy(true);
      try {
        const data = await createTechnicianRoadmapShare({
          technicianId,
          day,
          osSequence,
          technicianName: routeData?.technicianName || '',
        });
        const url = data?.url || '';
        shareCacheRef.current = {
          ...shareCacheRef.current,
          [day]: {
            url,
            fingerprint,
            validFrom: data?.validFrom || null,
            expiresAt: data?.expiresAt || null,
          },
        };
        openPanel({
          url,
          reused: false,
          validFrom: data?.validFrom || null,
          expiresAt: data?.expiresAt || null,
        });
      } catch (err) {
        alert(err?.message || 'Erro ao gerar link do roteiro.');
      } finally {
        setShareBusy(false);
      }
    };

    if (!isOpen) return null;

    return (
      <>
      <div className="modal-overlay">
        <div className="route-modal-content">
          <div className="route-modal-header">
            <div className="route-modal-title-section">
              <h2>
                <i className="bi bi-geo-alt"></i>
                Roteirização - {routeData?.technicianName}
              </h2>
              {isLoadingAllCoordinates && (
                <div className="coordinates-loading-indicator">
                  <i className="bi bi-arrow-repeat spin"></i>
                  <span>Carregando coordenadas...</span>
                </div>
              )}
            </div>
            <div className="route-modal-options">
              <div className="route-date-filter-selector">
                <div className={`route-date-filter-track${routeDateFilter === 'hoje' ? ' is-hoje' : ' is-amanha'}`}>
                  <div className="route-date-filter-thumb" aria-hidden="true" />
                  <button
                    type="button"
                    className={`route-date-filter-btn${routeDateFilter === 'amanha' ? ' is-active' : ''}`}
                    onClick={() => {
                      if (routeDateFilter === 'amanha') return;
                      if (routeDirty) {
                        const ok = window.confirm('Há alterações não salvas no roteiro. Deseja descartar e trocar o dia?');
                        if (!ok) return;
                      }
                      setRouteDateFilter('amanha');
                    }}
                  >
                    Para amanhã
                  </button>
                  <button
                    type="button"
                    className={`route-date-filter-btn${routeDateFilter === 'hoje' ? ' is-active' : ''}`}
                    onClick={() => {
                      if (routeDateFilter === 'hoje') return;
                      if (routeDirty) {
                        const ok = window.confirm('Há alterações não salvas no roteiro. Deseja descartar e trocar o dia?');
                        if (!ok) return;
                      }
                      setRouteDateFilter('hoje');
                    }}
                  >
                    Para hoje
                  </button>
                </div>
              </div>
              <label className="route-show-client-checkbox">
                <input
                  type="checkbox"
                  checked={showClientGrouping}
                  onChange={(e) => setShowClientGrouping(e.target.checked)}
                />
                <span>Exibir cliente</span>
              </label>
            </div>
            <button
              type="button"
              className="route-modal-save-btn"
              disabled={!routeDirty || savingBatch}
              onClick={handleSaveRouteBatch}
              title={routeDirty ? 'Salvar alterações do roteiro' : 'Nenhuma alteração para salvar'}
            >
              <i className={`bi ${savingBatch ? 'bi-arrow-repeat spin' : 'bi-check2-circle'}`}></i>
              {savingBatch ? 'Salvando...' : 'Salvar'}
            </button>
            <button 
              className="route-modal-close"
              onClick={requestCloseRouteModal}
              title="Fechar"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          <div className="route-modal-body">
            {loading && (
              <div className="route-loading">
                <div className="loading-spinner"></div>
                <p>Carregando ordens de serviço...</p>
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                  Preparando roteirização
                </span>
              </div>
            )}

            {error && (
              <div className="route-error">
                <i className="bi bi-exclamation-triangle"></i>
                <p>{error}</p>
              </div>
            )}

            {routeData && !loading && !error && (
              <div className="route-routing-container">
                {/* Coluna Esquerda - Ordens não roteirizadas */}
                <div className="route-column unrouted-column">
                  <div className="route-column-header">
                    <span className="route-count">{unroutedOrders.length}</span>
                    <div className="route-column-title-section">
                      <h3>Não Roteirizadas</h3>
                    </div>
                    <button 
                      className={`route-map-button ${unroutedOrders.length === 0 || isLoadingAllCoordinates ? 'disabled' : ''}`}
                      onClick={() => setShowUnroutedMap(true)}
                      disabled={unroutedOrders.length === 0 || isLoadingAllCoordinates}
                      title={unroutedOrders.length === 0 ? 'Nenhuma ordem disponível' : isLoadingAllCoordinates ? 'Carregando coordenadas...' : 'Roteirizar ordens pelo mapa'}
                    >
                      <i className="bi bi-geo-alt"></i>
                      <span>Roteirizar pelo Mapa</span>
                    </button>
                  </div>
                  <div 
                    className="route-column-content"
                    onDragOver={handleDragOver}
                    onDrop={handleDropToUnrouted}
                  >
                    {unroutedOrders.length === 0 ? (
                      <div className="route-empty-state">
                        <p>Nenhuma ordem disponível</p>
                      </div>
                    ) : (
                      <div className="route-orders-grouped">
                        {(() => {
                          const grouped = {};
                          unroutedOrders.forEach(order => {
                            const cidade = order.cidade || order.TB02115_CIDADE;
                            const bairro = order.bairro || order.TB02115_BAIRRO;
                            const cliente = getBoardClienteExibicao(order) || order.cliente || order.TB01008_NOME;
                            const endereco = order.endereco || order.TB02115_ENDERECO;
                            
                            // Agrupamento por cidade
                            if (!grouped[cidade]) {
                              grouped[cidade] = {
                                cidade,
                                bairros: {}
                              };
                            }
                            
                            // Sub-agrupamento por bairro dentro da cidade
                            if (!grouped[cidade].bairros[bairro]) {
                              grouped[cidade].bairros[bairro] = {
                                bairro,
                                clientes: {}
                              };
                            }
                            
                            // Sub-agrupamento por cliente dentro do bairro
                            if (!grouped[cidade].bairros[bairro].clientes[cliente]) {
                              grouped[cidade].bairros[bairro].clientes[cliente] = {
                                cliente,
                                endereco,
                                orders: []
                              };
                            }
                            grouped[cidade].bairros[bairro].clientes[cliente].orders.push(order);
                          });
                          return Object.values(grouped);
                        })().map((cidadeGroup, cidadeIndex) => (
                          <div key={cidadeIndex} className="route-client-group">
                            <div className="route-client-info">
                              <h4>{cidadeGroup.cidade}</h4>
                            </div>
                            {Object.values(cidadeGroup.bairros).map((bairroGroup, bairroIndex) => (
                              <div key={bairroIndex} className="route-bairro-subgroup">
                                <div className="route-bairro-info">
                                  <p>Bairro: {bairroGroup.bairro}</p>
                                </div>
                                {showClientGrouping ? (
                                  // Agrupamento por cliente quando checkbox marcado
                                  Object.values(bairroGroup.clientes).map((clienteGroup, clienteIndex) => (
                                    <div key={clienteIndex} className="route-cliente-subgroup route-cliente-subgroup-animated">
                                      <div className="route-cliente-info">
                                        <p>{clienteGroup.cliente}</p>
                                </div>
                                      <div className="route-orders-badges">
                                        {clienteGroup.orders.map((order, orderIndex) => (
                                          <div
                                            key={`${cidadeIndex}-${bairroIndex}-${clienteIndex}-${orderIndex}`}
                                            className={`route-order-badge ${hasLinkedOrder(order) ? 'has-linked-order' : ''}${hasDefeitoAlert(order) ? ' route-order-badge--defeito-alert' : ''}`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, order)}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOrderClick(order, e);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            {order.isRetorno && (
                                              <span className="order-retorno-icon-wrap order-retorno-icon-wrap--route-routing" title="Retorno" aria-hidden>
                                                <i className="bi bi-bell order-retorno-icon" />
                                              </span>
                                            )}
                                            {renderRouteTipoBadge(order)}
                                            <span className="route-order-id-bold">{order.id || order.TB02115_CODIGO}</span>
                                            {(order.TB01010_RESUMIDO || order.TB01010_NOME) && (
                                              <span className="route-order-resumido">
                                                {order.TB01010_RESUMIDO || order.TB01010_NOME}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  // Sem agrupamento por cliente (comportamento original)
                                <div className="route-orders-badges">
                                  {Object.values(bairroGroup.clientes).flatMap((clienteGroup, clienteIndex) =>
                                    clienteGroup.orders.map((order, orderIndex) => (
                                      <div
                                        key={`${cidadeIndex}-${bairroIndex}-${clienteIndex}-${orderIndex}`}
                                        className={`route-order-badge ${hasLinkedOrder(order) ? 'has-linked-order' : ''}${hasDefeitoAlert(order) ? ' route-order-badge--defeito-alert' : ''}`}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, order)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOrderClick(order, e);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {order.isRetorno && (
                                          <span className="order-retorno-icon-wrap order-retorno-icon-wrap--route-routing" title="Retorno" aria-hidden>
                                            <i className="bi bi-bell order-retorno-icon" />
                                          </span>
                                        )}
                                        {renderRouteTipoBadge(order)}
                                        <span className="route-order-id-bold">{order.id || order.TB02115_CODIGO}</span>
                                          {(order.TB01010_RESUMIDO || order.TB01010_NOME) && (
                                            <span className="route-order-resumido">
                                              {order.TB01010_RESUMIDO || order.TB01010_NOME}
                                            </span>
                                          )}
                                      </div>
                                    ))
                                  )}
                                </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Coluna Direita - Ordens roteirizadas */}
                <div className="route-column routed-column">
                  <div className="route-column-header">
                    <span className="route-count">{routedOrders.length}</span>
                    <div className="route-column-title-section">
                      <div className="route-title-with-date">
                        <h3>Roteirizadas</h3>
                        {routeData?.forecastDate && (
                          <span className="route-forecast-date">
                            {(() => {
                              let forecastDateObj;
                              
                              if (routeDateFilter === 'hoje') {
                                // Para hoje: usar data atual
                                forecastDateObj = new Date();
                              } else {
                                // Para amanhã: calcular a data de roteirização baseada no dia da semana atual
                                // Regras: Segunda a quinta = dia seguinte, Sexta/Sábado/Domingo = próxima segunda
                                // IMPORTANTE: Usar métodos locais para evitar problemas de fuso horário
                                const today = new Date();
                                const currentDay = today.getDay(); // 0 = Domingo, 1 = Segunda, ..., 6 = Sábado
                                const year = today.getFullYear();
                                const month = today.getMonth();
                                const day = today.getDate();
                                
                                if (currentDay >= 1 && currentDay <= 4) {
                                  // Segunda (1) a quinta (4): dia seguinte
                                  forecastDateObj = new Date(year, month, day + 1);
                                } else {
                                  // Sexta (5), sábado (6) ou domingo (0): próxima segunda-feira
                                  const daysUntilMonday = currentDay === 0 ? 1 : (8 - currentDay); // Domingo: +1, Sexta: +3, Sábado: +2
                                  forecastDateObj = new Date(year, month, day + daysUntilMonday);
                                }
                              }
                              
                              // Formatar para exibição brasileira (DD/MM/YYYY)
                              const displayDay = forecastDateObj.getDate().toString().padStart(2, '0');
                              const displayMonth = (forecastDateObj.getMonth() + 1).toString().padStart(2, '0');
                              const displayYear = forecastDateObj.getFullYear();
                              return `${displayDay}/${displayMonth}/${displayYear}`;
                            })()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="route-header-actions">
                      <button
                        type="button"
                        className="route-share-button"
                        onClick={shareRoute}
                        title="Compartilhar roteiro"
                        disabled={routedOrders.length === 0 || shareBusy}
                      >
                        <i className="bi bi-share"></i>
                      </button>
                      <button
                        type="button"
                        className="route-print-button"
                        onClick={printRoute}
                        title="Imprimir roteiro"
                        disabled={routedOrders.length === 0}
                      >
                        <i className="bi bi-printer"></i>
                      </button>
                    </div>
                  </div>
                  <div 
                    className="route-column-content"
                    onDragOver={handleDragOver}
                    onDrop={handleDropToRouted}
                  >
                    {routedOrders.length === 0 ? (
                      <div className="route-empty-state">
                        <p>Arraste ordens para criar a rota</p>
                      </div>
                    ) : (
                      <div className="route-orders-grouped">
                        {(() => {
                          const grouped = {};
                          routedOrders.forEach(order => {
                            const cidade = order.cidade || order.TB02115_CIDADE;
                            const bairro = order.bairro || order.TB02115_BAIRRO;
                            const cliente = getBoardClienteExibicao(order) || order.cliente || order.TB01008_NOME;
                            const endereco = order.endereco || order.TB02115_ENDERECO;
                            
                            // Agrupamento por cidade
                            if (!grouped[cidade]) {
                              grouped[cidade] = {
                                cidade,
                                bairros: {}
                              };
                            }
                            
                            // Sub-agrupamento por bairro dentro da cidade
                            if (!grouped[cidade].bairros[bairro]) {
                              grouped[cidade].bairros[bairro] = {
                                bairro,
                                clientes: {}
                              };
                            }
                            
                            // Sub-agrupamento por cliente dentro do bairro
                            if (!grouped[cidade].bairros[bairro].clientes[cliente]) {
                              grouped[cidade].bairros[bairro].clientes[cliente] = {
                                cliente,
                                endereco,
                                orders: []
                              };
                            }
                            grouped[cidade].bairros[bairro].clientes[cliente].orders.push(order);
                          });
                          return Object.values(grouped);
                        })().map((cidadeGroup, cidadeIndex) => (
                          <div key={cidadeIndex} className="route-client-group">
                            <div className="route-client-info">
                              <h4>{cidadeGroup.cidade}</h4>
                            </div>
                            {Object.values(cidadeGroup.bairros).map((bairroGroup, bairroIndex) => (
                              <div key={bairroIndex} className="route-bairro-subgroup">
                                <div className="route-bairro-info">
                                  <p>Bairro: {bairroGroup.bairro}</p>
                                </div>
                                {showClientGrouping ? (
                                  // Agrupamento por cliente quando checkbox marcado
                                  Object.values(bairroGroup.clientes).map((clienteGroup, clienteIndex) => (
                                    <div key={clienteIndex} className="route-cliente-subgroup route-cliente-subgroup-animated">
                                      <div className="route-cliente-info">
                                        <p>{clienteGroup.cliente}</p>
                                      </div>
                                      <div className="route-orders-badges">
                                        {clienteGroup.orders.map((order, orderIndex) => {
                                          const globalIndex = routedOrders.findIndex(o => 
                                            o.id === order.id || o.TB02115_CODIGO === order.TB02115_CODIGO
                                          );
                                          return (
                                            <div
                                              key={`${cidadeIndex}-${bairroIndex}-${clienteIndex}-${orderIndex}`}
                                              className={`route-order-badge ${hasLinkedOrder(order) ? 'has-linked-order' : ''}${hasDefeitoAlert(order) ? ' route-order-badge--defeito-alert' : ''}`}
                                              draggable
                                              onDragStart={(e) => handleDragStart(e, order)}
                                              onDragOver={(e) => handleDragOverRouted(e, globalIndex)}
                                              onDragLeave={handleDragLeave}
                                              onDrop={(e) => handleDropInRouted(e, globalIndex)}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOrderClick(order, e);
                                              }}
                                              style={{ cursor: 'pointer' }}
                                            >
                                              <span className="route-order-number">{order.routeOrder}</span>
                                              {order.isRetorno && (
                                                <span className="order-retorno-icon-wrap order-retorno-icon-wrap--route-routing" title="Retorno" aria-hidden>
                                                  <i className="bi bi-bell order-retorno-icon" />
                                                </span>
                                              )}
                                              {renderRouteTipoBadge(order)}
                                              <span className="route-order-id-bold">{order.id || order.TB02115_CODIGO}</span>
                                              {(order.TB01010_RESUMIDO || order.TB01010_NOME) && (
                                                <span className="route-order-resumido">
                                                  {order.TB01010_RESUMIDO || order.TB01010_NOME}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  // Sem agrupamento por cliente (comportamento original)
                                <div className="route-orders-badges">
                                  {Object.values(bairroGroup.clientes).flatMap((clienteGroup, clienteIndex) =>
                                    clienteGroup.orders.map((order, orderIndex) => {
                                      const globalIndex = routedOrders.findIndex(o => 
                                        o.id === order.id || o.TB02115_CODIGO === order.TB02115_CODIGO
                                      );
                                                                              return (
                                          <div
                                            key={`${cidadeIndex}-${bairroIndex}-${clienteIndex}-${orderIndex}`}
                                            className={`route-order-badge ${hasLinkedOrder(order) ? 'has-linked-order' : ''}${hasDefeitoAlert(order) ? ' route-order-badge--defeito-alert' : ''}`}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, order)}
                                            onDragOver={(e) => handleDragOverRouted(e, globalIndex)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDropInRouted(e, globalIndex)}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleOrderClick(order, e);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                          >
                                            <span className="route-order-number">{order.routeOrder}</span>
                                            {order.isRetorno && (
                                              <span className="order-retorno-icon-wrap order-retorno-icon-wrap--route-routing" title="Retorno" aria-hidden>
                                                <i className="bi bi-bell order-retorno-icon" />
                                              </span>
                                            )}
                                            {renderRouteTipoBadge(order)}
                                            <span className="route-order-id-bold">{order.id || order.TB02115_CODIGO}</span>
                                            {(order.TB01010_RESUMIDO || order.TB01010_NOME) && (
                                              <span className="route-order-resumido">
                                                {order.TB01010_RESUMIDO || order.TB01010_NOME}
                                              </span>
                                            )}
                                          </div>
                                        );
                                    })
                                  )}
                                </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal de Tooltip */}
        {showTooltip && tooltipOrder && (
          <div 
            className="order-tooltip-modal" 
            onClick={closeTooltip}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10000
            }}
          >
            <div 
              className="order-tooltip-content" 
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                left: tooltipPosition.x,
                top: tooltipPosition.y,
                transform: 'translateY(-50%)'
              }}
            >
              <div className="order-tooltip-header">
                <h3 className="order-tooltip-title">
                  Ordem {tooltipOrder.id || tooltipOrder.TB02115_CODIGO}
                </h3>
                <button className="order-tooltip-close" onClick={closeTooltip}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              
              <div className="order-tooltip-info">
                <div className="order-tooltip-item">
                  <span className="order-tooltip-label">Previsão:</span>
                  <span className="order-tooltip-value">
                    {tooltipOrder.previsao || 'N/A'}
                  </span>
                </div>
                
                <div className="order-tooltip-item">
                  <span className="order-tooltip-label">Cliente:</span>
                  <span className="order-tooltip-value">
                    {getRouteOrderClienteDisplay(tooltipOrder)}
                  </span>
                </div>
                
                <div className="order-tooltip-item">
                  <span className="order-tooltip-label">Contrato:</span>
                  <span className="order-tooltip-value">
                    {tooltipOrder.contrato || tooltipOrder.TB02115_CONTRATO || 'N/A'}
                  </span>
                </div>
                
                <div className="order-tooltip-item">
                  <span className="order-tooltip-label">Equipamento:</span>
                  <span className="order-tooltip-value">
                                            {tooltipOrder.equipamento || tooltipOrder.TB01010_RESUMIDO || tooltipOrder.TB01010_NOME || 'N/A'}
                  </span>
                </div>
                
                <div className="order-tooltip-item">
                  <span className="order-tooltip-label">Série:</span>
                  <span className="order-tooltip-value">
                    {tooltipOrder.numeroSerie || tooltipOrder.serie || tooltipOrder.TB02115_NUMSERIE || 'N/A'}
                  </span>
                </div>
                
                <div className="order-tooltip-item">
                  <span className="order-tooltip-label">Motivo da OS:</span>
                  <span className="order-tooltip-value">
                    {tooltipOrder.motivoOS || tooltipOrder.motivo || tooltipOrder.TB02115_NOME || 'N/A'}
                  </span>
                </div>
                
                <div className="order-tooltip-item">
                  <span className="order-tooltip-label">Solicitante:</span>
                  <span className="order-tooltip-value">
                    {tooltipOrder.solicitante || tooltipOrder.TB02115_SOLICITANTE || 'N/A'}
                  </span>
                </div>
                
                {/* Coordenadas */}
                {(() => {
                  const orderId = tooltipOrder.id || tooltipOrder.TB02115_CODIGO;
                  const coordinates = coordinatesCache[orderId];
                  const isLoading = loadingCoordinates[orderId];
                  const error = coordinatesError[orderId];
                  
                  if (isLoading) {
                    return (
                      <div className="order-tooltip-item">
                        <span className="order-tooltip-label">Coordenadas:</span>
                        <span className="order-tooltip-value">
                          <i className="bi bi-arrow-repeat spin" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                          Carregando...
                        </span>
                      </div>
                    );
                  }
                  
                  if (error) {
                    return (
                      <div className="order-tooltip-item">
                        <span className="order-tooltip-label">Coordenadas:</span>
                        <span className="order-tooltip-value" style={{ color: '#ef4444', fontSize: '10px' }}>
                          <i className="bi bi-exclamation-triangle" style={{ marginRight: '4px' }}></i>
                          Erro: {error}
                        </span>
                      </div>
                    );
                  }
                  
                  if (coordinates) {
                    return null;
                  }
                  
                  return (
                    <div className="order-tooltip-item">
                      <span className="order-tooltip-label">Coordenadas:</span>
                      <span className="order-tooltip-value" style={{ color: '#9ca3af', fontSize: '10px' }}>
                        <i className="bi bi-geo-alt" style={{ marginRight: '4px' }}></i>
                        Não disponível
                      </span>
                    </div>
                  );
                })()}
                
                {hasLinkedOrder(tooltipOrder) && (
                  <div className="order-tooltip-item order-tooltip-linked-order">
                    <span className="order-tooltip-label order-tooltip-linked-order-label">Pedido Vinculado:</span>
                    <span className="order-tooltip-value order-tooltip-linked-order-value">
                      {tooltipOrder.pedidoVinculado || tooltipOrder.TB02115_PEDIDO_VINCULADO || 'N/A'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal do Mapa de Ordens Não Roteirizadas */}
        {showUnroutedMap && (
          <div className="modal-overlay">
            <div className="route-map-modal-content">
              <div className="route-map-modal-header">
                <div className="route-map-modal-title-section">
                  <h2>
                    <i className="bi bi-geo-alt"></i>
                    Mapa - Roteirização
                  </h2>
                  {(() => {
                    const allOrders = [...unroutedOrders, ...routedOrders];
                    const ordersWithCoordinates = allOrders.filter(order => {
                      const orderId = order.id || order.TB02115_CODIGO;
                      return coordinatesCache[orderId];
                    });
                    
                    const locationGroups = groupOrdersByLocation(ordersWithCoordinates);
                    const uniqueLocations = Object.keys(locationGroups).length;
                    
                    return (
                      <div className="route-map-info">
                        <span className="route-map-count">
                          {uniqueLocations} pontos no mapa • {allOrders.length} ordens carregadas ({routedOrders.length} roteirizadas, {unroutedOrders.length} não roteirizadas)
                        </span>
                      </div>
                    );
                  })()}
                </div>
                <button 
                  className="route-modal-close"
                  onClick={() => setShowUnroutedMap(false)}
                  title="Fechar"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>

              <div className="route-map-modal-body">
                <div className="route-map-layout">
                  <div className="route-map-container">
                    <div id="unrouted-map" className="route-map"></div>
                  </div>
                  
                  {/* Painel lateral direito para roteirização */}
                  <div className="route-map-sidebar-right">
                    <div className="route-map-sidebar-header">
                      {(() => {
                        const progress = getMapRouteProgress();
                        return (
                          <div className="route-progress-info">
                            <div className="route-progress-bar">
                              <div 
                                className="route-progress-fill" 
                                style={{ width: `${progress.percentage}%` }}
                              ></div>
                            </div>
                            <div className="route-progress-text">
                              {progress.routed} de {progress.total} ({progress.percentage}%)
                            </div>
                            <div className="route-progress-remaining">
                              {progress.remaining} restantes • {progress.alreadyRouted} já roteirizadas
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    
                    <div className="route-map-sidebar-content">
                      {/* Seção para ordens já roteirizadas anteriormente */}
                      {routedOrders.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                          <h4 style={{ 
                            fontSize: '14px', 
                            color: '#475569', 
                            margin: '0 0 12px 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <i className="bi bi-check-circle-fill" style={{ color: '#059669' }}></i>
                            Ordens já roteirizadas ({routedOrders.length})
                          </h4>
                          <div className="route-orders-list">
                            {routedOrders.map((order, index) => (
                              <div key={`routed-${index}`} className="route-order-item" style={{ borderColor: '#059669', backgroundColor: '#f0fdf4' }}>
                                <div className="route-order-header">
                                  <div className="route-order-left">
                                    <span className="route-order-sequence" style={{ backgroundColor: '#059669' }}>{order.routeOrder}</span>
                                    <span className="route-order-number">
                                      OS {order.id || order.TB02115_CODIGO}
                                    </span>
                                  </div>
                                  <button 
                                    className="route-order-remove-btn"
                                    onClick={() => removeRoutedOrderFromMap(order)}
                                    title="Remover da roteirização"
                                    style={{ color: '#059669' }}
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                </div>
                                <div className="route-order-details">
                                  <p><strong>Cliente:</strong> {order.cliente || order.TB01008_NOME}</p>
                                  <p><strong>Endereço:</strong> {order.endereco || order.TB02115_ENDERECO}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Seção para ordens sendo roteirizadas agora */}
                      {mapRoutedOrders.length === 0 && routedOrders.length === 0 ? (
                        <div className="route-empty-state">
                          <i className="bi bi-info-circle"></i>
                          <p>Clique nos pontos do mapa e selecione "Incluir na rota" para começar a roteirização</p>
                        </div>
                      ) : mapRoutedOrders.length > 0 ? (
                        <div>
                          <h4 style={{ 
                            fontSize: '14px', 
                            color: '#475569', 
                            margin: '0 0 12px 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <i className="bi bi-clock" style={{ color: '#10b981' }}></i>
                            Roteirização atual ({mapRoutedOrders.length})
                          </h4>
                        <div className="route-orders-list">
                          {mapRoutedOrders.map((order, index) => (
                              <div key={`current-${index}`} className="route-order-item">
                              <div className="route-order-header">
                                <div className="route-order-left">
                                  <span className="route-order-sequence">{order.routeOrder}</span>
                                  <span className="route-order-number">
                                    OS {order.id || order.TB02115_CODIGO}
                                  </span>
                                </div>
                                <button 
                                  className="route-order-remove-btn"
                                  onClick={() => removeOrderFromMapRoute(order)}
                                  title="Remover da rota"
                                >
                                  <i className="bi bi-x"></i>
                                </button>
                              </div>
                              <div className="route-order-details">
                                <p><strong>Cliente:</strong> {order.cliente || order.TB01008_NOME}</p>
                                <p><strong>Endereço:</strong> {order.endereco || order.TB02115_ENDERECO}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        </div>
                      ) : null}
                      
                      {/* Botão para salvar roteirização (lote → save-batch) */}
                      {mapRoutedOrders.length > 0 && (
                        <div className="route-save-section">
                          <button 
                            className="route-save-btn"
                            disabled={savingRoute}
                            onClick={async () => {
                              try {
                                setSavingRoute(true);
                                setSaveProgress({ current: 0, total: 1 });
                                const forecastDate = getForecastDateByFilter();
                                const merged = [
                                  ...routedOrders.map((order, index) => ({
                                    order,
                                    sequence: index + 1,
                                  })),
                                  ...mapRoutedOrders.map((order, index) => ({
                                    order,
                                    sequence: routedOrders.length + index + 1,
                                  })),
                                ];
                                const items = merged
                                  .map(({ order, sequence }) => ({
                                    os: String(order.id || order.TB02115_CODIGO || '').trim(),
                                    sequence,
                                  }))
                                  .filter((it) => it.os);
                                const currentSet = new Set(items.map((it) => it.os));
                                const removedOs = (baselineRoutedOsRef.current || []).filter(
                                  (os) => os && !currentSet.has(os)
                                );

                                const token = localStorage.getItem('authToken');
                                const response = await fetch(`${API_BASE_URL}/api/route/save-batch`, {
                                  method: 'POST',
                                  headers: {
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'application/json',
                                  },
                                  body: JSON.stringify({
                                    technicianId: routeData.technicianId,
                                    forecast: forecastDate,
                                    source: 'route_modal',
                                    items,
                                    removedOs,
                                  }),
                                });
                                const result = await response.json();
                                if (!result.success) {
                                  throw new Error(result.message || 'Erro ao salvar roteiro');
                                }

                                setSaveProgress({ current: 1, total: 1 });
                                const newRoutedOrders = mapRoutedOrders.map((order, index) => ({
                                  ...order,
                                  routeOrder: routedOrders.length + index + 1,
                                  isRouted: true,
                                  routeSequence: routedOrders.length + index + 1,
                                }));
                                const routedOrderIds = mapRoutedOrders.map(
                                  (order) => order.id || order.TB02115_CODIGO
                                );
                                setRoutedOrders((prev) => [...prev, ...newRoutedOrders]);
                                setUnroutedOrders((prev) =>
                                  prev.filter((order) => {
                                    const orderId = order.id || order.TB02115_CODIGO;
                                    return !routedOrderIds.includes(orderId);
                                  })
                                );
                                baselineRoutedOsRef.current = items.map((it) => it.os);
                                setRouteDirty(false);
                                setShowUnroutedMap(false);
                                setMapRoutedOrders([]);
                                setSelectedMapOrders([]);
                                setMapRoutedSequence(1);
                                setMapTooltipOrder(null);
                                alert(`✅ Roteirização salva com sucesso!\n${items.length} ordens no roteiro.`);
                              } catch (error) {
                                console.error('❌ Erro ao salvar roteirização:', error);
                                alert('❌ Erro ao salvar roteirização: ' + (error.message || error));
                              } finally {
                                setSavingRoute(false);
                              }
                            }}
                          >
                            <i className={savingRoute ? "bi bi-hourglass-split" : "bi bi-check-circle"}></i>
                            {savingRoute ? `Salvando... (${saveProgress.current}/${saveProgress.total})` : 'Salvar Roteirização'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Painel lateral com ordens não localizadas e ignoradas fora do Brasil */}
                  {(() => {
                    const ordersWithoutCoordinates = unroutedOrders.filter(order => {
                      const orderId = order.id || order.TB02115_CODIGO;
                      return !coordinatesCache[orderId];
                    });
                    
                    const ordersIgnoredOutsideBrazil = unroutedOrders.filter(order => {
                      const orderId = order.id || order.TB02115_CODIGO;
                      const coordinates = coordinatesCache[orderId];
                      // Verificar se tem coordenadas mas não está no mapa (ignorada fora do Brasil)
                      return coordinates && coordinates.lat && coordinates.lng && 
                             !isValidBrazilianCoordinates(coordinates.lat, coordinates.lng);
                    });
                    
                    const totalUnlocatedOrders = ordersWithoutCoordinates.length + ordersIgnoredOutsideBrazil.length;
                    
                    if (totalUnlocatedOrders > 0) {
                      return (
                        <div className={`route-map-sidebar ${sidebarExpanded ? 'expanded' : 'collapsed'}`}>
                          <div className="sidebar-header">
                            <div className="sidebar-title-section">
                              <h4>
                                <i className="bi bi-exclamation-triangle"></i>
                                Ordens não localizadas ({totalUnlocatedOrders})
                              </h4>
                              <button 
                                className="sidebar-toggle-btn"
                                onClick={() => setSidebarExpanded(!sidebarExpanded)}
                                title={sidebarExpanded ? 'Recolher painel' : 'Expandir painel'}
                              >
                                <i className={`bi ${sidebarExpanded ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
                              </button>
                            </div>
                          </div>
                          
                          {sidebarExpanded && (
                            <div className="sidebar-content">
                              <div className="unlocated-orders-list">
                                {/* Ordens sem coordenadas */}
                                {ordersWithoutCoordinates.length > 0 && (
                                  <div className="unlocated-section">
                                    <div className="unlocated-section-header">
                                      <h5>
                                        <i className="bi bi-question-circle"></i>
                                        Sem coordenadas ({ordersWithoutCoordinates.length})
                                      </h5>
                                    </div>
                                    {/* Botão para refazer busca */}
                                    <div style={{ 
                                      padding: '8px 12px', 
                                      borderBottom: '1px solid #e2e8f0',
                                      backgroundColor: '#f8fafc'
                                    }}>
                                      <button
                                        onClick={retryFailedAddresses}
                                        disabled={isRetryingFailedAddresses}
                                        style={{
                                          width: '100%',
                                          padding: '8px 12px',
                                          backgroundColor: isRetryingFailedAddresses ? '#cbd5e1' : '#3b82f6',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '6px',
                                          fontSize: '13px',
                                          fontWeight: '500',
                                          cursor: isRetryingFailedAddresses ? 'not-allowed' : 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: '8px',
                                          transition: 'background-color 0.2s',
                                          marginBottom: retryResultMessage ? '8px' : '0'
                                        }}
                                        onMouseEnter={(e) => {
                                          if (!isRetryingFailedAddresses) {
                                            e.currentTarget.style.backgroundColor = '#2563eb';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!isRetryingFailedAddresses) {
                                            e.currentTarget.style.backgroundColor = '#3b82f6';
                                          }
                                        }}
                                      >
                                        {isRetryingFailedAddresses ? (
                                          <>
                                            <span style={{
                                              width: '14px',
                                              height: '14px',
                                              border: '2px solid white',
                                              borderTop: '2px solid transparent',
                                              borderRadius: '50%',
                                              animation: 'spin 1s linear infinite',
                                              display: 'inline-block'
                                            }}></span>
                                            <span>Buscando...</span>
                                          </>
                                        ) : (
                                          <>
                                            <i className="bi bi-arrow-clockwise"></i>
                                            <span>Refazer busca do(s) endereço(s)</span>
                                          </>
                                        )}
                                      </button>
                                      {/* Mensagem de resultado */}
                                      {retryResultMessage && (
                                        <div style={{
                                          padding: '8px 12px',
                                          borderRadius: '6px',
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          backgroundColor: retryResultMessage.type === 'success' ? '#d1fae5' : 
                                                         retryResultMessage.type === 'partial' ? '#fef3c7' : 
                                                         '#fee2e2',
                                          color: retryResultMessage.type === 'success' ? '#065f46' : 
                                                retryResultMessage.type === 'partial' ? '#92400e' : 
                                                '#991b1b',
                                          border: `1px solid ${retryResultMessage.type === 'success' ? '#a7f3d0' : 
                                                              retryResultMessage.type === 'partial' ? '#fde68a' : 
                                                              '#fecaca'}`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '6px'
                                        }}>
                                          <i className={`bi ${retryResultMessage.type === 'success' ? 'bi-check-circle-fill' : 
                                                              retryResultMessage.type === 'partial' ? 'bi-exclamation-triangle-fill' : 
                                                              'bi-x-circle-fill'}`}></i>
                                          <span>{retryResultMessage.text}</span>
                                        </div>
                                      )}
                                    </div>
                                    {ordersWithoutCoordinates.map((order, index) => (
                                      <div key={`no-coords-${index}`} className="unlocated-order-item">
                                        <div className="unlocated-order-header">
                                          <span className="unlocated-order-number">
                                            OS {order.id || order.TB02115_CODIGO}
                                          </span>
                                          <span className="unlocated-order-type">
                                            {getServiceTypeFromPreventiva(order.TB02115_PREVENTIVA)}
                                          </span>
                                        </div>
                                        <div className="unlocated-order-details">
                                          <div className="unlocated-order-cliente">
                                            {order.cliente || order.TB02115_CLIENTE || 'N/A'}
                                          </div>
                                          <div className="unlocated-order-equipamento">
                                            {order.equipamento || order.TB01010_RESUMIDO || order.TB01010_NOME || 'N/A'}
                                          </div>
                                          <div className="unlocated-order-address">
                                            {order.endereco || order.TB02115_ENDERECO || 'N/A'}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Ordens ignoradas fora do Brasil */}
                                {ordersIgnoredOutsideBrazil.length > 0 && (
                                  <div className="unlocated-section">
                                    <div className="unlocated-section-header">
                                      <h5>
                                        <i className="bi bi-globe"></i>
                                        Fora do Brasil ({ordersIgnoredOutsideBrazil.length})
                                      </h5>
                                    </div>
                                    {ordersIgnoredOutsideBrazil.map((order, index) => {
                                      const orderId = order.id || order.TB02115_CODIGO;
                                      const coordinates = coordinatesCache[orderId];
                                      return (
                                        <div key={`outside-brazil-${index}`} className="unlocated-order-item outside-brazil">
                                          <div className="unlocated-order-header">
                                            <span className="unlocated-order-number">
                                              OS {order.id || order.TB02115_CODIGO}
                                            </span>
                                            <span className="unlocated-order-type">
                                              {getServiceTypeFromPreventiva(order.TB02115_PREVENTIVA)}
                                            </span>
                                          </div>
                                          <div className="unlocated-order-details">
                                            <div className="unlocated-order-cliente">
                                              {order.cliente || order.TB02115_CLIENTE || 'N/A'}
                                            </div>
                                            <div className="unlocated-order-equipamento">
                                              {order.equipamento || order.TB01010_RESUMIDO || order.TB01010_NOME || 'N/A'}
                                            </div>
                                            <div className="unlocated-order-address">
                                              {order.endereco || order.TB02115_ENDERECO || 'N/A'}
                                            </div>
                                            {coordinates && (
                                              <div className="unlocated-order-coordinates">
                                                <small>
                                                  <i className="bi bi-geo-alt"></i>
                                                  Coordenadas encontradas: {coordinates.lat}, {coordinates.lng}
                                                </small>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            </div>

            {/* Tooltip do mapa */}
            {mapTooltipOrder && (
              <div 
                className="order-tooltip-modal" 
                onClick={() => setMapTooltipOrder(null)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 10001
                }}
              >
                <div 
                  className="order-tooltip-content" 
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    left: mapTooltipPosition.x,
                    top: mapTooltipPosition.y,
                    transform: 'translateY(-50%)'
                  }}
                >
                  <div className="order-tooltip-header">
                    <h3 className="order-tooltip-title">
                      {mapTooltipOrder._locationGroup ? 
                        `${mapTooltipOrder._locationGroup.length} ordens no local` : 
                        `Ordem ${mapTooltipOrder.id || mapTooltipOrder.TB02115_CODIGO}`
                      }
                    </h3>
                    <button className="order-tooltip-close" onClick={() => setMapTooltipOrder(null)}>
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                  
                  <div className="order-tooltip-info">
                    <div className="order-tooltip-item">
                      <span className="order-tooltip-label">Previsão:</span>
                      <span className="order-tooltip-value">
                        {mapTooltipOrder.previsao || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="order-tooltip-item">
                      <span className="order-tooltip-label">Cliente:</span>
                      <span className="order-tooltip-value">
                        {getRouteOrderClienteDisplay(mapTooltipOrder)}
                      </span>
                    </div>
                    
                    <div className="order-tooltip-item">
                      <span className="order-tooltip-label">Contrato:</span>
                      <span className="order-tooltip-value">
                        {mapTooltipOrder.contrato || mapTooltipOrder.TB02115_CONTRATO || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="order-tooltip-item">
                      <span className="order-tooltip-label">Equipamento:</span>
                      <span className="order-tooltip-value">
                        {mapTooltipOrder.equipamento || mapTooltipOrder.TB01010_RESUMIDO || mapTooltipOrder.TB01010_NOME || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="order-tooltip-item">
                      <span className="order-tooltip-label">Série:</span>
                      <span className="order-tooltip-value">
                        {mapTooltipOrder.numeroSerie || mapTooltipOrder.serie || mapTooltipOrder.TB02115_NUMSERIE || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="order-tooltip-item">
                      <span className="order-tooltip-label">Motivo da OS:</span>
                      <span className="order-tooltip-value">
                        {mapTooltipOrder.motivoOS || mapTooltipOrder.motivo || mapTooltipOrder.TB02115_NOME || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="order-tooltip-item">
                      <span className="order-tooltip-label">Solicitante:</span>
                      <span className="order-tooltip-value">
                        {mapTooltipOrder.solicitante || mapTooltipOrder.TB02115_SOLICITANTE || 'N/A'}
                      </span>
                    </div>
                    
                    {/* Lista de ordens do grupo (se houver múltiplas) */}
                    {mapTooltipOrder._locationGroup && mapTooltipOrder._locationGroup.length > 1 && (
                      <div className="order-tooltip-group">
                        <span className="order-tooltip-label">Ordens no local:</span>
                        <div className="order-tooltip-orders-list">
                          {mapTooltipOrder._locationGroup.map((order, index) => (
                            <div key={index} className="order-tooltip-order-item">
                              <span className="order-tooltip-order-number">
                                OS {order.id || order.TB02115_CODIGO}
                              </span>
                              <span className="order-tooltip-order-type">
                                {getServiceTypeFromPreventiva(order.TB02115_PREVENTIVA)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Coordenadas */}
                    {(() => {
                      const orderId = mapTooltipOrder.id || mapTooltipOrder.TB02115_CODIGO;
                      const coordinates = coordinatesCache[orderId];
                      const isLoading = loadingCoordinates[orderId];
                      const error = coordinatesError[orderId];
                      
                      if (isLoading) {
                        return (
                          <div className="order-tooltip-item">
                            <span className="order-tooltip-label">Coordenadas:</span>
                            <span className="order-tooltip-value">
                              <i className="bi bi-arrow-repeat spin" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                              Carregando...
                            </span>
                          </div>
                        );
                      }
                      
                      if (error) {
                        return (
                          <div className="order-tooltip-item">
                            <span className="order-tooltip-label">Coordenadas:</span>
                            <span className="order-tooltip-value" style={{ color: '#ef4444', fontSize: '10px' }}>
                              <i className="bi bi-exclamation-triangle" style={{ marginRight: '4px' }}></i>
                              Erro: {error}
                            </span>
                          </div>
                        );
                      }
                      
                      if (coordinates) {
                        return null;
                      }
                      
                      return (
                        <div className="order-tooltip-item">
                          <span className="order-tooltip-label">Coordenadas:</span>
                          <span className="order-tooltip-value" style={{ color: '#9ca3af', fontSize: '10px' }}>
                            <i className="bi bi-geo-alt" style={{ marginRight: '4px' }}></i>
                            Não disponível
                          </span>
                        </div>
                      );
                    })()}
                    
                    {hasLinkedOrder(mapTooltipOrder) && (
                      <div className="order-tooltip-item order-tooltip-linked-order">
                        <span className="order-tooltip-label order-tooltip-linked-order-label">Pedido Vinculado:</span>
                        <span className="order-tooltip-value order-tooltip-linked-order-value">
                          {mapTooltipOrder.pedidoVinculado || mapTooltipOrder.TB02115_PEDIDO_VINCULADO || 'N/A'}
                        </span>
                      </div>
                    )}
                    
                    {/* Botão de ação para roteirização */}
                    <div className="order-tooltip-actions">
                      {isOrderInMapRoute(mapTooltipOrder) ? (
                        <button 
                          className="order-tooltip-action-btn remove-route-btn"
                          onClick={() => {
                            removeOrderFromMapRoute(mapTooltipOrder);
                            setMapTooltipOrder(null);
                          }}
                        >
                          <i className="bi bi-dash-circle"></i>
                          Retirar da rota
                        </button>
                      ) : mapTooltipOrder.isRouted && mapTooltipOrder.routeSequence ? (
                        <button 
                          className="order-tooltip-action-btn remove-routed-btn"
                          onClick={() => {
                            removeRoutedOrderFromMap(mapTooltipOrder);
                            setMapTooltipOrder(null);
                          }}
                          style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }}
                        >
                          <i className="bi bi-x-circle"></i>
                          Remover da roteirização
                        </button>
                      ) : (
                        <button 
                          className="order-tooltip-action-btn add-route-btn"
                          onClick={() => {
                            addOrderToMapRoute(mapTooltipOrder);
                            setMapTooltipOrder(null);
                          }}
                        >
                          <i className="bi bi-plus-circle"></i>
                          Incluir na rota
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {routeDefeitoHover && ReactDOM.createPortal(
        <div
          className="board-cell-tooltip-portal"
          style={{
            position: 'fixed',
            left: routeDefeitoHover.x,
            top: routeDefeitoHover.y,
            transform: 'translateX(-50%)',
            zIndex: 100001,
            pointerEvents: 'none',
          }}
        >
          <span className="board-cell-tooltip-arrow" />
          <span className="board-cell-tooltip-text">{routeDefeitoHover.text}</span>
        </div>,
        document.body
      )}
      </>
    );
});
